import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { GameState } from "../core/game-state"
import { getHeroColorTheme } from "../core/hero-colors"
import { type InputAction, InputManager } from "../core/input-mapper"
import { AUDIO, EVENT, SCENE, TEX } from "../core/keys"
import {
  fadeMusicOut,
  returnToWaveMusic,
  setMusicWaveVolume,
  startBossMusic,
  startMusicSequence,
} from "../core/music-volume"
import { setRectBodyInWorldPixels } from "../core/physics-utils"
import { THEME } from "../core/theme"
import { getWaveObserverSettings } from "../core/wave-observer"
import { getSectorBackground } from "../data/backgrounds"
import { CHARACTERS, getHeroDefinitionById } from "../data/characters"
import {
  BUMPER_PROGRESSION,
  COMBO_TARGET_HITS,
  DEFAULT_BALL_DEF,
  FIREBALL_AURA_RADIUS,
  FIREBALL_BOSS_DAMAGE_RATIO,
  FLAP_POWER,
  FORCE_FIELD_ENABLED,
  FORCE_FIELD_X,
  GAME_HEIGHT,
  GAME_WIDTH,
  GRAVITY,
  INVULNERABILITY_TIME,
  MAX_FALL_SPEED,
  MAX_WAVES,
  OVERDRIVE_TIME_SCALE,
  SCENE_TUNING,
  SPRITE_SIZES,
  getWaveDuration,
} from "../data/constants"
import { getWaveAnnouncementMessage } from "../data/nudges"
import { SECTOR_WAVE_SPAN, isBossWave } from "../data/sectors"
import { STAT_UPGRADES } from "../data/upgrades"
import { resolveBossEncounterStats } from "../systems/boss-encounter-stats"
import { BossSystem } from "../systems/boss-system"
import { createEnemy } from "../systems/enemies/enemy-registry"
import {
  EnemySystem,
  RUNTIME_ENEMY_DEFINITIONS,
  spawnWaveEnemyEntry,
} from "../systems/enemy-system"
import { PortalManager } from "../systems/portal-manager"
import { getSfxPlayer } from "../systems/sfx-generator"
import { TextureManager } from "../systems/texture-manager"
import { VfxManager } from "../systems/vfx-manager"
import type {
  Character,
  EnemyType,
  GameData,
  GameSprite,
  HeroControlProfile,
  Player,
  PowerMode,
  RunStage,
  SectorModifier,
  Upgrade,
} from "../types"
// NOTE: utils.ts will be created in src/utils.ts during migration. These are
// duplicated from lib/game/utils.ts and must match the canonical implementations.
import {
  applyUpgrade,
  createExpCoin,
  createPlayer,
  getRandomUpgrades,
  tryRevivePlayer,
} from "../utils"
import { DEBUG_GAME_SPEED } from "./debug-scene"

const COLOR = THEME.colors

import { BumperHits } from "../systems/bumper-hits"
import { BumperSystem } from "../systems/bumper-system"
import { CollisionRules } from "../systems/collision-rules"
import { CombatBallSystem } from "../systems/combat-ball-system"
import { EconomySystem } from "../systems/economy-system"
import { EnemyFlow } from "../systems/enemy-system"
import { FieldRuntime } from "../systems/field-runtime"
import { ObserverBridge } from "../systems/observer-bridge"
import { PlayerRuntime } from "../systems/player-runtime"
import { PowerFlow } from "../systems/power-runtime"
import { PropSystem } from "../systems/prop-system"
import { RunTransitions } from "../systems/run-transitions"
import {
  createLoadedAnimations,
  queueSectorBackgroundAssets,
  queueWaveAssets,
} from "../systems/runtime-assets"
import { SectorEffects } from "../systems/sector-effects"
import { SfxRuntime } from "../systems/sfx-runtime"
import { HudSync } from "../systems/wave-hud-sync"
import { WaveTimeline } from "../systems/wave-state"
import { waveStrategy } from "../systems/wave-strategy"
import {
  type FilterableGameObject,
  NEON,
  applyForceFieldShimmer,
  applyNoiseAtmosphere,
  applyThresholdPass,
  createSectorOverlay,
} from "../ui/neon-fx"
import type { HudScene } from "./hud-scene"
import type { WaveDebugInspectState, WaveRuntimeApi } from "./wave-runtime-api"

interface WavePointerFields {
  pointer?: { upTime: number; downTime: number }
  currentlyOver?: Array<{ getData: (key: string) => unknown }>
}
type WaveInputEvent = (KeyboardEvent & WavePointerFields) | WavePointerFields

export class WaveScene extends Phaser.Scene implements WaveRuntimeApi {
  // ── State ──────────────────────────────────────────────────────
  private gameData!: GameData
  private character!: Character
  private requestedWave = 1
  private isTesting = false
  private testOverlay: string | null = null
  private testingState: "none" | "initializing" | "paused" = "none"
  private debugInvincible = false
  private readonly waveObserver = getWaveObserverSettings()
  private isRunEnding = false
  private lastDamageSource: "enemy" | "projectile" | "floor" | "generic" =
    "generic"
  private lastHurtByBoss = false

  private sectorOverlay?: Phaser.GameObjects.Container | null
  private backgroundGradient?: Phaser.GameObjects.GameObject | null
  private activeSectorId = ""
  private loadingSectorBackgroundId = ""
  // ── Core Runtime ───────────────────────────────────────────────
  private textureManager!: TextureManager
  private vfx!: VfxManager
  private enemies!: EnemySystem
  private waveTimeline!: WaveTimeline
  private bumperSystem!: BumperSystem
  private propSystem!: PropSystem
  private economy!: EconomySystem
  private run!: RunTransitions
  private field!: FieldRuntime
  private collisions!: CollisionRules
  private enemyFlow!: EnemyFlow
  private bossSystem!: BossSystem
  private playerRuntime!: PlayerRuntime
  private sectorFx!: SectorEffects
  private power!: PowerFlow
  private sfx!: SfxRuntime
  private bumperHits!: BumperHits
  private observer!: ObserverBridge

  // ── Physics groups ─────────────────────────────────────────────
  private enemyGroup!: Phaser.Physics.Arcade.Group
  private projectileGroup!: Phaser.Physics.Arcade.Group
  private bumperGroup!: Phaser.Physics.Arcade.Group
  private propGroup!: Phaser.Physics.Arcade.Group
  private expCoinGroup!: Phaser.Physics.Arcade.Group
  private combatBallGroup!: Phaser.Physics.Arcade.Group

  // ── Combat ball systems ──────────────────────────────────────
  private combatBallSystem!: CombatBallSystem

  // ── Sprites & visuals ──────────────────────────────────────────
  private dogSprite!: GameSprite
  private bgLayers: Phaser.GameObjects.TileSprite[] = []
  private bgLayerBaseY: number[] = []
  private bgNoiseOverlay?: Phaser.GameObjects.NoiseSimplex2D | null
  private bgDispNoise?: Phaser.GameObjects.NoiseSimplex2D | null
  private readonly BG_DISP_TEX_KEY = "bg_disp_noise"
  private bgCellNoise?: Phaser.GameObjects.NoiseCell4D | null
  private bgRiverReflection?: Phaser.GameObjects.TileSprite | null
  private bgStarContainer?: Phaser.GameObjects.Container | null
  private bgCityLights?: Phaser.GameObjects.Container | null
  private bgCityLightsRepeatPx = 0
  private bgScanlines?: Phaser.GameObjects.Graphics | null
  private bgShootingStarTimer?: Phaser.Time.TimerEvent | null
  private readonly BG_CELL_TEX_KEY = "bg_cell_normal"
  private forceField?: Phaser.GameObjects.Graphics
  private aimLine!: Phaser.GameObjects.Graphics
  private chargeOrbGraphics!: Phaser.GameObjects.Graphics
  private isAiming = false
  private controllerAimAngleDeg: number | null = null
  private keyboardAimLeft = false
  private keyboardAimRight = false
  private keyboardAimUp = false
  private keyboardAimDown = false
  private spacePressedAt = 0
  private lastGamepadDownAt = 0
  private chargeStartTime = 0
  private lastChargeSparkTime = 0
  private chargeLastSoundLevel = 0
  private static readonly FLAP_SHOOT_THRESHOLD_MS = 150
  private static readonly CHARGE_LEVEL1_MS = 400
  private static readonly CHARGE_LEVEL2_MS = 1000

  // ── Timers / cooldowns ───────────────────────────────────────────
  private static readonly PICKUP_SFX_COOLDOWN_MS = 60
  private enemySpawnTimer = 0
  private bumperSpawnTimer = 0
  private slingshotSpawnTimer = 0
  private lastPickupSfxAt = Number.NEGATIVE_INFINITY
  private bossSpawned = false
  private bossDefeatSummaryStarted = false

  // ── Sector modifier state ─────────────────────────────────────
  private sectorModifier: SectorModifier | undefined
  private darknessOverlay?: Phaser.GameObjects.Graphics
  private _spawnCycleCount = 0
  private _debugFrozen = false
  private _infiniteTime = false
  private hudSync!: HudSync
  private inputManager!: InputManager
  private lastComboEscalationSignature = ""
  constructor() {
    super({ key: SCENE.WAVE })
  }

  // ══════════════════════════════════════════════════════════════
  //  LIFECYCLE
  // ══════════════════════════════════════════════════════════════
  private isPortal = false

  init(data: {
    wave?: number
    isTesting?: boolean
    testOverlay?: string
    isPortal?: boolean
  }): void {
    this.requestedWave = data?.wave ?? 1
    this.isTesting = data?.isTesting ?? false
    this.testOverlay = data?.testOverlay ?? null
    this.isPortal = data?.isPortal ?? false
    this.isRunEnding = false
  }
  preload(): void {
    queueWaveAssets(this, this.requestedWave)
  }

  create(): void {
    if (this.scene.isActive(SCENE.MENU)) {
      this.scene.stop(SCENE.MENU)
    }
    if (this.scene.isActive(SCENE.STORY)) {
      this.scene.stop(SCENE.STORY)
    }
    if (this.scene.isActive(SCENE.CHARACTER_SELECT)) {
      this.scene.stop(SCENE.CHARACTER_SELECT)
    }

    this.character = this.resolveCharacter()
    this.textureManager = new TextureManager(this)
    this.textureManager.createProceduralTextures()
    createLoadedAnimations(this)
    this.initializeWaveTimeline()
    this.initGameData()
    this.economy = new EconomySystem(this.gameData)
    this.hudSync = new HudSync({
      registry: this.game.registry,
      getGameData: () => this.gameData,
      getWaveTimeline: () => this.waveTimeline,
      getCombatBallSystem: () => this.combatBallSystem,
      getProjectileGroup: () => this.projectileGroup,
      getBossSystem: () => this.bossSystem,
      getPowerDuration: (mode) => this.power.getDuration(mode),
    })
    this.createPhysicsGroups()
    this.initializeVisualSystems()
    this.initializeWaveRuntime()
    this.initializeCombatBallSystem()
    this.registerRuntimeEventBridges()
    this.setupSceneState()
    this.sfx.register()
    this.activateObserverMode()
    // Ball is now launched on flap — no auto-launch

    this.registerDebugCommands()

    this.events.once("shutdown", () => this.onShutdown())
  }

  private registerDebugCommands(): void {
    const onWarp = (wave: number) => {
      this.gameData.player.hp = 0 // Force a reset or just warp directly?
      // For warp, maybe we just set wave and restart scene:
      if (wave > 0 && wave <= 20) {
        this.scene.restart({ wave })
      }
    }
    const onKillAll = () => {
      for (const child of this.enemyGroup.getChildren()) {
        const enemySprite = child as GameSprite
        if (enemySprite.active) {
          enemySprite.setData("hp", 0)
          this.enemyFlow.defeatEnemy(enemySprite)
        }
      }
    }
    const onGiveMoney = (amount: number) => {
      this.gameData.player.exp += amount || 1000
      this.hudSync.sync()
    }
    const onSetHp = (amount: number) => {
      this.gameData.player.maxHp = amount || 9999
      this.gameData.player.hp = amount || 9999
      this.hudSync.sync()
    }
    const onSetAllStatsLevel = (targetLevel: number) => {
      this.setAllStatsDebugLevel(targetLevel || 5)
    }
    const onReloadScene = () => {
      this.scene.restart({ wave: this.getActiveWaveNumber() })
    }
    const onSpawnEnemy = (payload: {
      type: EnemyType
      x?: number
      y?: number
    }) => {
      if (!payload?.type) return
      const spawnX = payload.x ?? GAME_WIDTH + 40
      const spawnY = payload.y ?? GAME_HEIGHT * 0.5
      this.spawnEnemyInternal(payload.type, spawnX, spawnY, 1, "encounter")
    }
    const onToggleInvincible = (enabled: boolean) => {
      this.debugInvincible = !!enabled
    }

    EventBus.on("debug-warp-to-wave", onWarp)
    EventBus.on("debug-kill-all-enemies", onKillAll)
    EventBus.on("debug-give-money", onGiveMoney)
    EventBus.on("debug-set-hp", onSetHp)
    EventBus.on("debug-set-all-stats-level", onSetAllStatsLevel)
    EventBus.on("debug-reload-scene", onReloadScene)
    EventBus.on("debug-spawn-enemy", onSpawnEnemy)
    EventBus.on("debug-toggle-invincible", onToggleInvincible)

    this.events.once("shutdown", () => {
      EventBus.off("debug-warp-to-wave", onWarp)
      EventBus.off("debug-kill-all-enemies", onKillAll)
      EventBus.off("debug-give-money", onGiveMoney)
      EventBus.off("debug-set-hp", onSetHp)
      EventBus.off("debug-set-all-stats-level", onSetAllStatsLevel)
      EventBus.off("debug-reload-scene", onReloadScene)
      EventBus.off("debug-spawn-enemy", onSpawnEnemy)
      EventBus.off("debug-toggle-invincible", onToggleInvincible)
    })
  }

  private resolveCharacter(): Character {
    return GameState.instance.character ?? CHARACTERS[0]
  }

  private getActiveWaveNumber(): number {
    return this.waveTimeline ? this.waveTimeline.getWave() : this.requestedWave
  }

  private getSpawnDifficultyMultiplier(
    waveNumber = this.getActiveWaveNumber(),
  ) {
    if (this.waveTimeline && waveNumber === this.waveTimeline.getWave()) {
      return this.waveTimeline.getEnemyDifficultyMultiplier()
    }

    return waveStrategy.getEnemyDifficultyMultiplier(waveNumber)
  }

  private initializeWaveTimeline(): void {
    this.waveTimeline = new WaveTimeline()
    this.waveTimeline.reset(1)
    for (let w = 1; w < this.requestedWave; w++) {
      if (isBossWave(w)) {
        this.waveTimeline.completeBossWave()
      } else {
        this.waveTimeline.update(getWaveDuration(w) + 1)
      }
      this.waveTimeline.advanceWave()
    }
  }

  private createPhysicsGroups(): void {
    this.enemyGroup = this.physics.add.group()
    this.projectileGroup = this.physics.add.group()
    this.bumperGroup = this.physics.add.group()
    this.propGroup = this.physics.add.group()
    this.expCoinGroup = this.physics.add.group()
    this.combatBallGroup = this.physics.add.group({ maxSize: 20 })
  }

  private initializeVisualSystems(): void {
    this.bgLayers = []
    this.bgLayerBaseY = []
    this.vfx = new VfxManager(this)
    this.vfx.createSystems()
    this.createParallaxBackground()
    this.createArenaWalls()
    this.createPlayerSprite()
    this.vfx.applyBaseCameraFx()

    this.aimLine = this.add.graphics()
    this.aimLine.setDepth(45)

    this.chargeOrbGraphics = this.add.graphics()
    this.chargeOrbGraphics.setDepth(95)
    this.chargeOrbGraphics.setBlendMode(Phaser.BlendModes.ADD)
  }

  private initializeWaveRuntime(): void {
    this.enemies = new EnemySystem(
      this,
      this.enemyGroup,
      this.projectileGroup,
      this.vfx,
      () => this.waveTimeline.getEnemyDifficultyMultiplier(),
      createEnemy,
    )
    this.bumperSystem = new BumperSystem(this, this.bumperGroup)
    this.propSystem = new PropSystem(this, this.propGroup, this.vfx)
    this.run = new RunTransitions({
      scene: this,
      economy: this.economy,
      vfx: this.vfx,
      enemyGroup: this.enemyGroup,
      projectileGroup: this.projectileGroup,
      expCoinGroup: this.expCoinGroup,
      getDogSprite: () => this.dogSprite,
      getGameData: () => this.gameData,
      getWaveNumber: () => this.getActiveWaveNumber(),
      isObserverMode: () => this.observer.enabled,
      restartObserverWave: (reason) => this.observer.restart(reason),
      handleVictory: () => this.handleVictory(),
      collectCoin: (value, x, y) => this.collectCoin(value, x, y),
      clearPowerMode: () => this.power.deactivate(false),
      setMovementStateOverride: (state) =>
        this.playerRuntime.setMovementStateOverride(state),
      advanceWave: () => this.waveTimeline.advanceWave(),
      syncWaveMetadata: () => this.syncWaveMetadata(),
      getCurrentSector: () => this.waveTimeline.getCurrentSector(),
      spawnPortal: (x, y, type) => this.spawnPortal(x, y, type),
    })
    this.field = new FieldRuntime({
      projectileGroup: this.projectileGroup,
      expCoinGroup: this.expCoinGroup,
      bumperGroup: this.bumperGroup,
      bumperSystem: this.bumperSystem,
      vfx: this.vfx,
      getGameData: () => this.gameData,
      getDogSprite: () => this.dogSprite,
      getProjectileEffectColor: (projectile) =>
        this.getProjectileEffectColor(projectile),
      onHandleBumperHit: (bumper) => this.bumperHits.handleHit(bumper),
    })
    this.collisions = new CollisionRules({
      getNow: () => this.time.now,
      getPlayerPowerMode: () => this.gameData.player.powerMode ?? null,
      isPlayerInvulnerable: () => this.gameData.player.isInvulnerable,
      isBossEnemy: (enemy) => this.isBossEnemy(enemy),
      defeatEnemy: (enemy) => this.enemyFlow.defeatEnemy(enemy),
      damageBossEnemy: (enemy, ratio, color) =>
        this.damageBossEnemy(enemy, ratio, color),
      handlePlayerDamage: (damage, source) =>
        this.handlePlayerDamage(damage, source),
      createImpactBurst: (x, y, color, scale) =>
        this.vfx.emitImpactBurst(x, y, color, scale),
      collectCoin: (value, x, y) => this.collectCoin(value, x, y),
      onCoinPickup: (coin) => {
        this.vfx.pickupEmitter.explode(8, coin.x, coin.y)
        const coinData = coin.entityData
        if (coinData?.kind === "coin") {
          this.vfx.showDamageNumber(
            coin.x,
            coin.y - 10,
            coinData.value,
            COLOR.success,
          )
        }
      },
      projectileInfoColor: COLOR.info,
      projectileDangerColor: COLOR.danger,
    })
    this.playerRuntime = new PlayerRuntime({
      scene: this,
      vfx: this.vfx,
      getGameData: () => this.gameData,
      getDogSprite: () => this.dogSprite,
      onPlayerDamage: (damage, source) =>
        this.handlePlayerDamage(damage, source),
      onDestroyThreatsTouchingPlayer: () => this.destroyThreatsTouchingPlayer(),
    })
    this.sectorFx = new SectorEffects({
      scene: this,
      enemies: this.enemies,
      vfx: this.vfx,
      getGameData: () => this.gameData,
      getDogSprite: () => this.dogSprite,
      getSectorModifier: () => this.sectorModifier,
      getWaveNumber: () => this.getActiveWaveNumber(),
      getDarknessOverlay: () => this.darknessOverlay,
      updateDarknessOverlay: (alpha) => this.updateDarknessOverlay(alpha),
    })
    this.bossSystem = new BossSystem({
      scene: this,
      enemyGroup: this.enemyGroup,
      vfx: this.vfx,
      spawner: this.enemies,
      playSfx: (key, vol) => this.playSfx(key, vol),
      spawnBumperBurst: (count) => {
        const gameData = this.gameData
        for (let i = 0; i < count; i++) {
          this.bumperSystem.spawnBumper(gameData.player.bumperWeights)
        }
      },
      getWaveNumber: () => this.getActiveWaveNumber(),
      getPhaseHandler: (type) => RUNTIME_ENEMY_DEFINITIONS[type].bossPhase,
      onSnakeSplit: (snake) => {
        this.time.delayedCall(0, () => {
          if (snake.active) this.enemyFlow.defeatEnemy(snake)
        })
      },
    })
    this.enemyFlow = new EnemyFlow({
      scene: this,
      enemyGroup: this.enemyGroup,
      expCoinGroup: this.expCoinGroup,
      enemies: this.enemies,
      vfx: this.vfx,
      bumperSystem: this.bumperSystem,
      economy: this.economy,
      bossSystem: this.bossSystem,
      getGameData: () => this.gameData,
      getDogSprite: () => this.dogSprite,
      getWaveNumber: () => this.getActiveWaveNumber(),
      playSfx: (key, vol) => this.playSfx(key, vol),
      spawnCoin: (x, y, value) => this.spawnCoin(x, y, value),
      onGathererEscape: () => this.waveTimeline.reportGathererEscape(),
      onBossDefeated: () => this.handleBossDefeated(),
      damagePlayer: (damage) => this.handlePlayerDamage(damage, "generic"),
    })
    this.bumperHits = new BumperHits({
      scene: this,
      vfx: this.vfx,
      enemyGroup: this.enemyGroup,
      bumperSystem: this.bumperSystem,
      getGameData: () => this.gameData,
      getDogSprite: () => this.dogSprite,
      onBumperHitSound: () => this.sfx.playBumperHit(),
      onActivatePower: (effect) => this.power.activate(effect),
      onDefeatEnemy: (enemy) => this.enemyFlow.defeatEnemy(enemy),
      onDamageBossEnemy: (enemy, ratio, color) =>
        this.damageBossEnemy(enemy, ratio, color),
      spawnCoins: (x, y, value) => this.spawnCoin(x, y, value),
      healPlayer: (amount) => this.handlePlayerHeal(amount),
      grantXp: (amount) => this.economy.grantXp(amount),
    })
    this.observer = new ObserverBridge({
      scene: this,
      waveObserver: this.waveObserver,
      getGameData: () => this.gameData,
      getWaveNumber: () => this.getActiveWaveNumber(),
      getCharacter: () => this.character,
      waveTimeline: this.waveTimeline,
      enemyGroup: this.enemyGroup,
      getEnemyPopulationCap: () => this.getEnemyPopulationCap(),
      setRunStage: (next) => this.setRunStage(next),
    })
    this.power = new PowerFlow({
      scene: this,
      vfx: this.vfx,
      getGameData: () => this.gameData,
      getDogSprite: () => this.dogSprite,
      getCombatBallSystem: () => this.combatBallSystem,
      bumperSystem: this.bumperSystem,
      playSfx: (key, vol) => this.playSfx(key, vol),
      shoot: () => this.shoot(),
      destroyThreatsTouchingPlayer: () => this.destroyThreatsTouchingPlayer(),
      activatePowerMode: (mode, duration) =>
        this.playerRuntime.activatePowerMode(mode, duration),
      clearPowerMode: () => this.playerRuntime.clearPowerMode(),
      suppressBumperHitSfx: (durationMs) =>
        this.sfx?.suppressBumperHits(durationMs),
    })
    this.sfx = new SfxRuntime({
      getNow: () => this.time.now,
      vfx: this.vfx,
      getGameData: () => this.gameData,
      playSfx: (key, vol) => this.playSfx(key, vol),
    })
  }

  private initializeCombatBallSystem(): void {
    const ballDef = DEFAULT_BALL_DEF
    const heroProfile: HeroControlProfile = this.character.controlProfile ?? {
      redirectStrength: ballDef.redirectStrength,
      speedMultiplier: 1.0,
      recoveryDuration: ballDef.recoveryDuration,
      comboAcceleration: 1.0,
      forgiveness: "medium" as const,
    }

    this.combatBallSystem = new CombatBallSystem(
      this,
      this.combatBallGroup,
      ballDef,
      this.bumperSystem,
      this.vfx,
      heroProfile,
      (enemySprite) => this.enemyFlow.defeatEnemy(enemySprite),
      (enemySprite, damage) =>
        this.bossSystem.applyBallDamageToBrain(enemySprite, damage, (e) =>
          this.enemyFlow.defeatEnemy(e),
        ),
    )

    this.syncCombatBallProgression()
  }

  private syncCombatBallProgression(): void {
    this.hudSync.setBallPresentation(0xf97316)
  }

  private setupCombatBallCollisions(): void {
    // Combat ball collisions (only wired when launchers are active)
    this.physics.add.overlap(
      this.combatBallGroup,
      this.dogSprite,
      (ball, dog) =>
        this.combatBallSystem.onPlayerContact(
          ball as Phaser.Physics.Arcade.Sprite,
          dog as Phaser.Physics.Arcade.Sprite,
        ),
    )
    this.physics.add.overlap(
      this.combatBallGroup,
      this.enemyGroup,
      (ball, enemy) =>
        this.combatBallSystem.onEnemyContact(
          ball as Phaser.Physics.Arcade.Sprite,
          enemy as Phaser.Physics.Arcade.Sprite,
        ),
    )
    this.physics.add.overlap(
      this.combatBallGroup,
      this.bumperGroup,
      (ball, bumper) => {
        const bumperSprite = bumper as GameSprite

        // Ball physics (rebound + hit gain)
        this.combatBallSystem.onBumperContact(
          ball as Phaser.Physics.Arcade.Sprite,
          bumper as Phaser.Physics.Arcade.Sprite,
        )

        this.bumperSystem.onBallHitBumper(bumperSprite)

        this.gameData.totalBumperHits++
        this.sfx.playBumperHit()
      },
    )
  }

  private setupSceneState(): void {
    this.hudSync.reset()
    this.setupCollisions()
    this.setupInputHandlers()
    this.cameras.main.setBounds(0, 0, GAME_WIDTH, GAME_HEIGHT)
    this.cameras.main.setScroll(0, 0)
    this.restartHudScene()
    this.vfx.clearComboEscalation()
    this.vfx.clearBossIntro()
    this.lastComboEscalationSignature = ""
    this.enemySpawnTimer = 0
    this.bumperSpawnTimer = 0
    this.slingshotSpawnTimer = 0
    this.bossSpawned = false
    this.bossDefeatSummaryStarted = false
    this.hudSync.sync()
    startMusicSequence(this, true, !this.waveTimeline.isBossWave())
    this.sectorModifier = this.waveTimeline.getCurrentSector().modifier
    this.sectorFx.reset()
    this.enemyFlow.resetBossPhaseTracking()
    this.economy.pendingLevelUpChoices.length = 0

    if (this.isTesting) {
      this.testingState = "initializing"
    } else {
      this.startWaveIntro()
    }
  }

  private registerRuntimeEventBridges(): void {
    EventBus.on(EVENT.POWER_MODE_CHANGED, this.handlePowerModeChanged, this)
    EventBus.on(EVENT.HERO_STATE_CHANGED, this.handleHeroStateChanged, this)
    EventBus.on(EVENT.RUN_STATE_CHANGED, this.handleRunStateChanged, this)
    EventBus.on(EVENT.COMBO_TIER_REACHED, this.handleComboTierReached, this)
    EventBus.on(EVENT.PLAYER_LEVELUP, this.handlePlayerLevelUp, this)
  }

  private handlePowerModeChanged(mode: PowerMode): void {
    this.hudSync.setPowerMode(mode)
  }

  private handleHeroStateChanged(state: string): void {
    this.hudSync.setHeroMovementState(state)
  }

  private handleRunStateChanged(state: RunStage): void {
    if (state === "playing") return

    this.isAiming = false
    this.aimLine.clear()
    this.vfx.stopHeroLaunchExhaust()
  }

  private handleComboTierReached(payload: {
    tier: number
    hits: number
    targetHits: number
  }): void {
    // Shine all active balls when a combo tier is crossed
    for (const child of this.combatBallGroup.getChildren()) {
      const ball = child as GameSprite
      if (ball.active) {
        Phaser.Actions.AddEffectShine(ball, {
          duration: 300 + payload.tier * 80,
          direction: 0,
          colorFactor: [2, 2, 2, 1],
        })
      }
    }
  }

  private handlePlayerLevelUp(): void {
    // mid-wave level up is disabled, they will appear at end of wave
    // this.run.triggerMidWaveLevelUp()
  }

  private setRunStage(next: RunStage): void {
    this.gameData.state = next
    GameState.instance.runState = next
  }

  private restartHudScene(): void {
    if (this.scene.isActive(SCENE.HUD)) {
      this.scene.stop(SCENE.HUD)
    }
    this.scene.launch(SCENE.HUD)
  }

  private activateObserverMode(): void {
    if (!this.waveObserver.enabled) return

    this.gameData.player.isInvulnerable = true
    this.gameData.player.invulnerabilityTimer = Number.MAX_SAFE_INTEGER
    this.observer.postReady()
    this.observer.postState(true)
  }

  update(time: number, rawDelta: number): void {
    if (this.testingState === "initializing") {
      // Run parallax update once to ensure background is drawn
      this.updateParallax(rawDelta)
      this.testingState = "paused"
      this.physics.pause()
      // this.systems.pause() // systems.pause() does not exist in Phaser 3/4 Scene.Systems

      if (this.testOverlay) {
        this.launchTestOverlay()
      }
      return
    }
    if (this.testingState === "paused") return
    if (this._debugFrozen) return
    if (
      this.gameData.state === "waveIntro" ||
      this.gameData.state === "waveSummary"
    ) {
      const delta = rawDelta * DEBUG_GAME_SPEED
      this.updateParallax(delta)
      this.cameras.main.setScroll(0, 0)
      this.vfx.updateTrails(delta)
      this.hudSync.sync()
      return
    }
    if (this.gameData.state !== "playing") return

    let gameTimeScale = 1.0
    if (this.gameData.player.powerMode === "overdrive") {
      gameTimeScale = OVERDRIVE_TIME_SCALE
    }

    const playerDelta = rawDelta * DEBUG_GAME_SPEED
    const delta = playerDelta * gameTimeScale

    this.updateParallax(delta)
    this.updatePlayer(time, playerDelta) // Player moves at normal speed
    this.cameras.main.setScroll(0, 0)
    this.updateEnemies(time, delta)
    this.propSystem.update(time, delta)
    this.enemyFlow.updateToxicZones(time, delta)
    this.updateProjectiles(time, delta)

    // Update all combat ball systems
    this.combatBallSystem.update(playerDelta)

    // Keep the old projectile cap behavior tied to equivalent progress time.
    this.enemies.waveTimer = this.waveTimeline.getRemainingTime()
    this.updateSpawning(delta)
    this.updateWave(delta)
    this.updateExpCoins(delta)
    this.updateBumpers(time, delta)
    this.bumperSystem.updateBumperStun(delta)
    this.checkBumperCollisions()
    this.power.update(playerDelta)
    this.updateSectorModifiers(delta)
    this.vfx.updateTrails(delta)
    this.checkGameState()
    this.updateDirectionalAim()
    this.observer.postState()
    this.updateChargeVisuals()
    this.updateAimLine()
    this.updateComboEscalationVisuals()

    // Sync to HUD registry every frame (dirty-check inside HudScene)
    this.hudSync.sync()
  }

  private updateAimLine(): void {
    this.aimLine.clear()

    const isOverdrive = this.isOverdriveActive()
    const primarySystem = this.combatBallSystem
    if (
      (this.isAiming || isOverdrive) &&
      this.gameData.state === "playing" &&
      primarySystem
    ) {
      const now = this.time.now
      const baseAngleDeg = this.getCurrentAimAngleDeg()
      const spreadAngles = primarySystem.getSpreadAngles()
      const length = GAME_WIDTH * 2
      const g = this.aimLine

      for (const spread of spreadAngles) {
        const angleDeg = baseAngleDeg + spread
        const angleRad = Phaser.Math.DegToRad(angleDeg)
        const cosA = Math.cos(angleRad)
        const sinA = Math.sin(angleRad)

        const startX = this.dogSprite.x + cosA * 40
        const startY = this.dogSprite.y + sinA * 40

        if (isOverdrive) {
          // ── OVERDRIVE: full plasma beam ─────────────────────────
          const pulse = 0.7 + Math.sin(now / 70) * 0.3
          const fastPulse = 0.8 + Math.sin(now / 40) * 0.2
          const color1 = NEON.cyan
          const color2 = NEON.magenta
          const scroll = (now / 8) % 48

          // Wide plasma glow (continuous beam, not dashed)
          g.lineStyle(32, color1, 0.06 * pulse)
          g.beginPath()
          g.moveTo(startX, startY)
          g.lineTo(startX + cosA * length, startY + sinA * length)
          g.strokePath()

          // Mid beam
          g.lineStyle(14, color1, 0.18 * pulse)
          g.beginPath()
          g.moveTo(startX, startY)
          g.lineTo(startX + cosA * length, startY + sinA * length)
          g.strokePath()

          // Chromatic edge — offset perpendicular beams for split-color effect
          const perpOff = 3 * pulse
          const pX = -sinA * perpOff
          const pY = cosA * perpOff
          g.lineStyle(2, color2, 0.35 * fastPulse)
          g.beginPath()
          g.moveTo(startX + pX, startY + pY)
          g.lineTo(startX + cosA * length + pX, startY + sinA * length + pY)
          g.strokePath()
          g.lineStyle(2, color2, 0.35 * fastPulse)
          g.beginPath()
          g.moveTo(startX - pX, startY - pY)
          g.lineTo(startX + cosA * length - pX, startY + sinA * length - pY)
          g.strokePath()

          // Hot white core
          g.lineStyle(2.5, 0xffffff, 0.85 * fastPulse)
          g.beginPath()
          g.moveTo(startX, startY)
          g.lineTo(startX + cosA * length, startY + sinA * length)
          g.strokePath()

          // Muzzle origin — layered pulsing rings
          g.fillStyle(color1, 0.35 * pulse)
          g.fillCircle(startX, startY, 20 * pulse)
          g.lineStyle(3, color2, 0.6 * pulse)
          g.strokeCircle(startX, startY, 26 + Math.sin(now / 90) * 3)
          g.lineStyle(1.5, 0xffffff, 0.85)
          g.strokeCircle(startX, startY, 10 + Math.sin(now / 70) * 2)
          g.fillStyle(0xffffff, 0.9)
          g.fillCircle(startX, startY, 3)

          // Scrolling energy markers along the beam
          for (let i = 0; i < 8; i++) {
            const t = ((i * 48 + scroll) % length) / length
            if (t > 1) continue
            const mx = startX + cosA * length * t
            const my = startY + sinA * length * t
            const mRadius = 4 + Math.sin(now / 100 + i * 1.2) * 1.5
            const mColor = i % 2 === 0 ? color1 : color2
            const mAlpha = (1 - t) * 0.55 * pulse

            g.fillStyle(mColor, mAlpha)
            g.fillCircle(mx, my, mRadius)
            g.lineStyle(1.5, 0xffffff, mAlpha * 0.6)
            g.strokeCircle(mx, my, mRadius + 3)
          }
        } else {
          // ── NORMAL: thin dotted line ────────────────────────────
          const lineColor = NEON.green
          const dotSpacing = 14
          const dotCount = Math.floor(length / dotSpacing)

          // Faint outer glow dots
          for (let i = 0; i < dotCount; i++) {
            const t = (i * dotSpacing) / length
            if (t > 1) break
            const fade = Math.max(0, 1 - t * 0.8)
            const dx = startX + cosA * dotSpacing * i
            const dy = startY + sinA * dotSpacing * i
            g.fillStyle(lineColor, 0.12 * fade)
            g.fillCircle(dx, dy, 3)
          }
          // Core dots
          for (let i = 0; i < dotCount; i++) {
            const t = (i * dotSpacing) / length
            if (t > 1) break
            const fade = Math.max(0, 1 - t * 0.8)
            const dx = startX + cosA * dotSpacing * i
            const dy = startY + sinA * dotSpacing * i
            g.fillStyle(lineColor, 0.4 * fade)
            g.fillCircle(dx, dy, 1.5)
          }
          // Hot white center dots (sparser)
          for (let i = 0; i < dotCount; i += 2) {
            const t = (i * dotSpacing) / length
            if (t > 1) break
            const fade = Math.max(0, 1 - t * 0.9)
            const dx = startX + cosA * dotSpacing * i
            const dy = startY + sinA * dotSpacing * i
            g.fillStyle(0xffffff, 0.45 * fade)
            g.fillCircle(dx, dy, 0.8)
          }
        }
      }
    }
  }

  // ══════════════════════════════════════════════════════════════
  //  INIT HELPERS
  // ══════════════════════════════════════════════════════════════

  private initGameData(): void {
    const gs = GameState.instance
    const waveNumber = this.getActiveWaveNumber()
    const waveDuration = this.waveTimeline.getCurrentWaveDuration()
    const pushTargetDistance = Math.max(
      1,
      Math.round((waveDuration / 1000) * SCENE_TUNING.pushDistanceRateBase),
    )

    // Re-use player from shop continuation or portal entry, else create fresh
    let player: Player
    if (gs.player && (waveNumber > 1 || this.isPortal)) {
      player = gs.player
    } else {
      player = createPlayer(this.character)
    }
    player.waveGuardCharges = player.passives.waveGuardPerWave

    this.gameData = {
      state: "waveIntro",
      player,
      enemies: [],
      projectiles: [],
      expCoins: [],
      bumpers: [],
      wave: waveNumber,
      waveDuration,
      waveTimer: 0,
      pushDistance: 0,
      pushTargetDistance,
      pushProgress: 0,
      runDistance: gs.runDistance,
      score: gs.score,
      scoreThisWave: 0,
      coins: gs.coins,
      coinsThisWave: 0,
      enemiesKilledThisWave: 0,
      scrollSpeed:
        SCENE_TUNING.initialScrollSpeed *
        this.waveTimeline.getCurrentSector().scrollMultiplier,
      upgradeChoices: [],
      selectedCharacter: this.character,
      totalBumperHits: 0,
      bottomHitCount: 0,
    }

    this.gameData.player.combo.state =
      this.gameData.player.combo.state ?? "normal"
    this.gameData.player.combo.hits = this.gameData.player.combo.hits ?? 0
    this.gameData.player.combo.targetHits =
      this.gameData.player.combo.targetHits ?? COMBO_TARGET_HITS
    this.gameData.player.combo.nextEffect =
      this.gameData.player.combo.nextEffect ?? "charge"
    this.gameData.player.combo.timer = this.gameData.player.combo.timer ?? 0
  }

  private createPlayerSprite(): void {
    const playerTexture = `char_${this.character.id}`
    const hero = getHeroDefinitionById(this.character.id)
    this.dogSprite = this.physics.add.sprite(
      GAME_WIDTH * 0.12,
      GAME_HEIGHT * 0.5,
      playerTexture,
    ) as GameSprite

    this.dogSprite.setDisplaySize(
      SPRITE_SIZES.player_sprite.w,
      SPRITE_SIZES.player_sprite.h,
    )
    if (hero.animation) {
      this.dogSprite.play(hero.animation.key)
    }

    const colorStr = GameState.instance.playerColor
    if (colorStr) {
      const color = Phaser.Display.Color.HexStringToColor(colorStr).color
      this.dogSprite.setTint(color)
    }

    const body = this.dogSprite.body as Phaser.Physics.Arcade.Body
    body.setGravityY(GRAVITY * SCENE_TUNING.playerGravityScale)
    body.setBounce(SCENE_TUNING.playerBounceX, SCENE_TUNING.playerBounceY)
    body.setMaxVelocity(
      SCENE_TUNING.playerMaxVelocityX,
      MAX_FALL_SPEED * SCENE_TUNING.playerMaxFallVelocityScale,
    )
    body.setCollideWorldBounds(true)
    body.setBoundsRectangle(
      new Phaser.Geom.Rectangle(
        SCENE_TUNING.playerBoundsMarginX,
        SCENE_TUNING.playerBoundsMarginTop,
        GAME_WIDTH -
          SCENE_TUNING.playerBoundsMarginX -
          SCENE_TUNING.playerBoundsMarginRight,
        GAME_HEIGHT -
          SCENE_TUNING.playerBoundsMarginTop -
          SCENE_TUNING.playerBoundsMarginBottom,
      ),
    )
    setRectBodyInWorldPixels(
      this.dogSprite,
      SCENE_TUNING.playerBodyWidth,
      SCENE_TUNING.playerBodyHeight,
      SCENE_TUNING.playerBodyOffsetX,
      SCENE_TUNING.playerBodyOffsetY,
    )

    // Neon glow — gives the hero a soft emissive edge on the gameplay field
    this.dogSprite.enableFilters()
    const heroGlowColor = Phaser.Display.Color.HexStringToColor(
      hero.glowColor,
    ).color
    this.dogSprite.filters?.internal.addGlow(
      heroGlowColor,
      3,
      0.5,
      1,
      false,
      8,
      12,
    )
  }

  private spawnPortal(_x: number, _y: number, _type: string): void {
    const portalX = _x
    const portalY = _y
    const portalRadius = 30

    const portalContainer = this.add.container(portalX, portalY)
    portalContainer.setDepth(10)

    const glow = this.add.graphics()
    const swirl = this.add.graphics()

    const drawPortal = (time: number) => {
      glow.clear()
      swirl.clear()

      const pulseScale = 1 + Math.sin(time * 0.003) * 0.1

      glow.fillStyle(0x00ffaa, 0.15 * pulseScale)
      glow.fillCircle(0, 0, portalRadius * 1.8 * pulseScale)
      glow.fillStyle(0x00ffaa, 0.25 * pulseScale)
      glow.fillCircle(0, 0, portalRadius * 1.4 * pulseScale)
      glow.fillStyle(0x00dd88, 0.4 * pulseScale)
      glow.fillCircle(0, 0, portalRadius * pulseScale)

      const segments = 6
      for (let i = 0; i < segments; i++) {
        const angle = (i / segments) * Math.PI * 2 + time * 0.002
        const x1 = Math.cos(angle) * portalRadius * 0.3
        const y1 = Math.sin(angle) * portalRadius * 0.3
        const x2 = Math.cos(angle) * portalRadius * 0.9 * pulseScale
        const y2 = Math.sin(angle) * portalRadius * 0.9 * pulseScale
        swirl.lineStyle(3, 0xaaffcc, 0.8)
        swirl.beginPath()
        swirl.moveTo(x1, y1)
        swirl.lineTo(x2, y2)
        swirl.strokePath()
      }

      swirl.lineStyle(2, 0xffffff, 0.6)
      swirl.strokeCircle(0, 0, portalRadius * 0.4)
      swirl.lineStyle(3, 0x00ffaa, 0.9)
      swirl.strokeCircle(0, 0, portalRadius * 0.7 * pulseScale)
      swirl.lineStyle(2, 0x00dd88, 0.7)
      swirl.strokeCircle(0, 0, portalRadius)
    }

    drawPortal(0)

    const portalManager = PortalManager.instance

    const hitArea = this.add.circle(portalX, portalY, portalRadius * 1.5)
    hitArea.setInteractive(
      new Phaser.Geom.Circle(0, 0, portalRadius * 1.5),
      "pointerdown",
    )
    hitArea.setAlpha(0)
    hitArea.setDepth(11)

    hitArea.on("pointerdown", () => {
      const exitUrl = portalManager.getExitPortalUrl()
      if (exitUrl) {
        window.location.href = exitUrl
      }
    })

    let portalTime = 0
    const portalTimer = this.time.addEvent({
      delay: 16,
      callback: () => {
        portalTime += 16
        drawPortal(portalTime)
      },
      loop: true,
    })

    this.time.delayedCall(5000, () => {
      portalTimer.remove()
      this.tweens.add({
        targets: portalContainer,
        alpha: 0,
        scale: 2,
        duration: 500,
        onComplete: () => {
          glow.destroy()
          swirl.destroy()
          portalContainer.destroy()
          hitArea.destroy()
        },
      })
    })
  }

  private createParallaxBackground(): void {
    this.rebuildSectorBackground(this.waveTimeline.getCurrentSector().id)

    if (FORCE_FIELD_ENABLED) {
      this.forceField = this.add.graphics()
      this.forceField.lineStyle(2, COLOR.indigo, 0.15)
      this.forceField.lineBetween(FORCE_FIELD_X, 0, FORCE_FIELD_X, GAME_HEIGHT)
      this.forceField.setDepth(-5)

      applyForceFieldShimmer(
        this.forceField as unknown as FilterableGameObject,
        NEON.cyan,
      )
    }
  }

  private rebuildSectorBackground(sectorId: string): void {
    const sectorBackground = getSectorBackground(sectorId)
    const hasMissingLayer = sectorBackground.layers.some(
      (layer) => !this.textures.exists(layer.key),
    )
    if (hasMissingLayer) {
      this.loadSectorBackground(sectorId)
      return
    }

    for (const layer of this.bgLayers) {
      layer.destroy()
    }
    this.bgLayers = []
    this.bgLayerBaseY = []

    this.bgNoiseOverlay?.destroy()
    this.bgNoiseOverlay = null

    this.bgDispNoise?.destroy()
    this.bgDispNoise = null
    if (this.textures.exists(this.BG_DISP_TEX_KEY)) {
      this.textures.remove(this.BG_DISP_TEX_KEY)
    }

    this.bgCellNoise?.destroy()
    this.bgCellNoise = null
    this.bgRiverReflection?.destroy()
    this.bgRiverReflection = null
    this.bgStarContainer?.destroy()
    this.bgStarContainer = null
    this.bgCityLights?.destroy()
    this.bgCityLights = null
    this.bgCityLightsRepeatPx = 0
    this.bgShootingStarTimer?.destroy()
    this.bgShootingStarTimer = null
    this.bgScanlines?.destroy()
    this.bgScanlines = null
    if (this.textures.exists(this.BG_CELL_TEX_KEY)) {
      this.textures.remove(this.BG_CELL_TEX_KEY)
    }

    this.sectorOverlay?.destroy()
    this.sectorOverlay = null

    this.backgroundGradient?.destroy()
    this.backgroundGradient = null

    this.activeSectorId = sectorId

    // ── Base Gradient Layer (Atmosphere) ──────────────────────────
    const gradColor = sectorId.includes("void")
      ? [0, 0, 0, 0.8]
      : sectorId === "moon"
        ? [0.02, 0.03, 0.06, 0.98]
        : [0.1, 0.1, 0.2, 0.8]
    this.backgroundGradient = this.add
      .gradient(
        {
          bands: [
            {
              start: 0,
              end: 1,
              colorStart: gradColor,
              colorEnd: [0.05, 0.05, 0.1, 1],
            },
          ],
          direction: Math.PI / 2,
        },
        GAME_WIDTH / 2,
        GAME_HEIGHT / 2,
        GAME_WIDTH,
        GAME_HEIGHT,
      )
      .setDepth(-20)
      .setScrollFactor(0)

    sectorBackground.layers.forEach((layer, i) => {
      const textureSource = this.textures.get(layer.key).getSourceImage() as
        | { width?: number; height?: number }
        | undefined
      const sourceHeight = textureSource?.height ?? GAME_HEIGHT
      const renderHeight = layer.renderHeight ?? GAME_HEIGHT
      const uniformScale = renderHeight / sourceHeight
      const baseY = GAME_HEIGHT / 2 + (layer.yOffset ?? 0) - renderHeight / 2

      const tile = this.add.tileSprite(
        GAME_WIDTH / 2,
        baseY,
        GAME_WIDTH,
        renderHeight,
        layer.key,
      )
      tile.setTileScale(uniformScale, uniformScale)
      const foreground = layer.foreground === true
      const depth = foreground ? 50 + i : -10 + i
      const alpha = layer.alpha ?? 1
      tile.setScrollFactor(0).setDepth(depth).setAlpha(alpha)
      this.bgLayers.push(tile)
      this.bgLayerBaseY.push(baseY)
    })

    // ── Subtle displacement on background tile layers ────────────
    // A small noisesimplex2d renders to a shared texture; mid and far
    // (non-foreground) layers get a Displacement filter driven by that texture.
    this.bgDispNoise = this.add
      .noisesimplex2d(
        { noiseCells: [4, 4], noiseIterations: 2, noiseNormalMap: true },
        0,
        0,
        128,
        72,
      )
      .setVisible(false)
      .setRenderToTexture(this.BG_DISP_TEX_KEY)

    sectorBackground.layers.forEach((layer, i) => {
      const tile = this.bgLayers[i]
      if (tile && !layer.foreground && i > 0) {
        tile.filters?.internal.addDisplacement(
          this.BG_DISP_TEX_KEY,
          0.008,
          0.006,
        )
      }
    })

    // ── Sector 1: cellular noise wet-pavement reflection ────────
    // ── Sector 1: river reflection ───────────────────────────────
    // layer-0 has a dark river band at ~55–63% of its height.
    // At game scale (540/768 ≈ 0.703) that maps to screen y ≈ 387–478.
    // We place a flipped TileSprite of layer-1 (city skyline) there at
    // depth -9.5 — behind the buildings (-9) but in front of the sky (-10).
    // Through the transparent regions of layer-1 the rippling reflection
    // of the skyline is visible, creating the illusion of a city river.
    if (sectorId === "occupied-skyline") {
      // Buildings (layer-1, renderHeight=260): bottom anchored at y≈342.
      // River zone starts BELOW that with a clear gap.
      // RIVER_Y=450, RIVER_H=140 → spans y=380–520; well clear of y=342.
      const RIVER_Y = 415
      const RIVER_H = 140
      const layer1Key = "bg_occupied_skyline_1"
      const layer1Def = sectorBackground.layers[1]
      const srcImg1 = this.textures.get(layer1Key).getSourceImage() as
        | { height?: number }
        | undefined
      const srcH1 = srcImg1?.height ?? GAME_HEIGHT
      const renderH1 = layer1Def?.renderHeight ?? GAME_HEIGHT
      const tileScale1 = renderH1 / srcH1

      // 4D cellular noise normal map rendered to shared texture.
      // noiseNormalScale:2 gives stronger ripple normals.
      this.bgCellNoise = this.add
        .noisecell4d(
          {
            noiseCells: [4, 32, 4, 4],
            noiseIterations: 4,
            noiseNormalMap: true,
            noiseNormalScale: 2,
          },
          0,
          0,
          GAME_WIDTH,
          GAME_HEIGHT,
        )
        .setVisible(false)
        .setRenderToTexture(this.BG_CELL_TEX_KEY)

      // Flipped layer-1 TileSprite — building reflections on the water.
      // Cyan tint (0x33aacc) reads more as water shimmer than plain blue.
      // Displacement scale 0.08/0.03 gives clearly visible wave distortion.
      this.bgRiverReflection = this.add
        .tileSprite(GAME_WIDTH / 2, RIVER_Y, GAME_WIDTH, RIVER_H, layer1Key)
        .setTileScale(tileScale1, tileScale1)
        .setFlipY(true)
        .setAlpha(0.8)
        .setTint(0x33aacc)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(-9.4)
        .setScrollFactor(0)

      this.bgRiverReflection
        .enableFilters()
        .filters?.external.addDisplacement(this.BG_CELL_TEX_KEY, 0.08, 0.03)

      // ── Pixel-art twinkling stars ─────────────────────────────
      // depth -9.8: behind buildings (-9), in front of sky (-10).
      // Stars in the building zone peek through transparent silhouette gaps.
      const starContainer = this.add.container(0, 0)
      starContainer.setDepth(-9.8).setScrollFactor(0)
      const starPalette = [0xffffff, 0xaaccff, 0xffffcc, 0xffd8aa]
      const SKY_ZONE_H = 220

      for (let i = 0; i < 60; i++) {
        const sx = Phaser.Math.Between(0, GAME_WIDTH)
        const sy = Phaser.Math.Between(4, SKY_ZONE_H)
        const size = Phaser.Math.Between(1, 2)
        const color = Phaser.Utils.Array.GetRandom(starPalette) as number
        const star = this.add.rectangle(sx, sy, size, size, color)
        starContainer.add(star)
        this.tweens.add({
          targets: star,
          alpha: {
            from: Phaser.Math.FloatBetween(0.05, 0.35),
            to: Phaser.Math.FloatBetween(0.75, 1),
          },
          duration: Phaser.Math.Between(700, 2200),
          yoyo: true,
          repeat: -1,
          delay: Phaser.Math.Between(0, 2000),
          ease: "Sine.easeInOut",
        })
      }

      // Bright 4-pointed cross sparkles — classic pixel-art accent
      for (let i = 0; i < 8; i++) {
        const sx = Phaser.Math.Between(20, GAME_WIDTH - 20)
        const sy = Phaser.Math.Between(4, SKY_ZONE_H - 10)
        const color = Phaser.Utils.Array.GetRandom([
          0xffffff, 0xaaddff,
        ]) as number
        const h = this.add.rectangle(sx, sy, 5, 1, color)
        const v = this.add.rectangle(sx, sy, 1, 5, color)
        starContainer.add([h, v])
        this.tweens.add({
          targets: [h, v],
          alpha: { from: 0.2, to: 1 },
          duration: Phaser.Math.Between(1000, 2500),
          yoyo: true,
          repeat: -1,
          delay: Phaser.Math.Between(0, 2500),
          ease: "Quad.easeInOut",
        })
      }

      this.bgStarContainer = starContainer

      // ── SNES shmup atmosphere ──────────────────────────────────

      // Horizon edge glow — thin cyan ADD line where buildings meet water
      this.add
        .rectangle(GAME_WIDTH / 2, 344, GAME_WIDTH, 6, 0x00ccff)
        .setAlpha(0.2)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDepth(-9.3)
        .setScrollFactor(0)

      // City window lights — colored blinking pixels parallaxed with layer-1.
      // Lights tile every repeatPx (= srcWidth × tileScaleX) so they always
      // align with the repeating building texture as it scrolls.
      const srcImgW = this.textures.get(layer1Key).getSourceImage() as
        | { width?: number }
        | undefined
      const srcW = srcImgW?.width ?? 1376
      const repeatPx = srcW * tileScale1 // ≈ 322 screen px
      const nRepeats = Math.ceil(GAME_WIDTH / repeatPx) + 2 // enough to fill screen
      this.bgCityLightsRepeatPx = repeatPx

      const lightsContainer = this.add.container(0, 0)
      lightsContainer.setDepth(-8.8).setScrollFactor(0)
      const lightColors = [
        0xffcc44, 0xff8833, 0x00ffcc, 0xaaddff, 0xff6688, 0xffffff,
      ]
      const totalLights = 120
      for (let i = 0; i < totalLights; i++) {
        // Spread evenly across nRepeats × repeatPx so they tile with the texture
        const lx = (i / totalLights) * (nRepeats * repeatPx)
        // Building silhouettes (layer-1 spans screen y=162-342, opaque pixels
        // start around y=272-301 depending on column). Stay below y=280 to be
        // safely inside building bodies, not in the transparent sky above them.
        const ly = Phaser.Math.Between(283, 336)
        const lw = i % 8 === 0 ? 3 : Phaser.Math.Between(1, 2)
        const color = Phaser.Utils.Array.GetRandom(lightColors) as number
        const onAlpha = Phaser.Math.FloatBetween(0.55, 1)
        const light = this.add.rectangle(lx, ly, lw, 1, color).setAlpha(onAlpha)
        lightsContainer.add(light)
        this.tweens.add({
          targets: light,
          alpha: { from: onAlpha, to: onAlpha * 0.1 },
          duration: Phaser.Math.Between(80, 200),
          yoyo: true,
          repeat: -1,
          delay: Phaser.Math.Between(0, 6000),
          repeatDelay: Phaser.Math.Between(1000, 5000),
          ease: "Linear",
        })
      }
      this.bgCityLights = lightsContainer

      // Shooting stars — periodic pixel streaks across the night sky
      const spawnShootingStar = () => {
        const sx = Phaser.Math.Between(300, GAME_WIDTH)
        const sy = Phaser.Math.Between(12, 180)
        const len = Phaser.Math.Between(18, 48)
        const g = this.add.graphics()
        g.lineStyle(1, 0xffffff, 1)
        g.lineBetween(0, 0, -len, 2)
        g.setPosition(sx, sy).setDepth(-9.7).setScrollFactor(0)
        this.tweens.add({
          targets: g,
          x: sx - len * 4,
          y: sy + 10,
          alpha: 0,
          duration: Phaser.Math.Between(320, 620),
          ease: "Quad.easeIn",
          onComplete: () => {
            try {
              g.destroy()
            } catch (_) {}
          },
        })
      }
      const scheduleNextStar = () => {
        this.bgShootingStarTimer = this.time.delayedCall(
          Phaser.Math.Between(2500, 7000),
          () => {
            spawnShootingStar()
            if (Math.random() > 0.5) {
              this.time.delayedCall(
                Phaser.Math.Between(100, 500),
                spawnShootingStar,
              )
            }
            scheduleNextStar()
          },
        )
      }
      scheduleNextStar()

      // CRT scanlines — subtle alternating rows for authentic SNES look
      const scanlines = this.add.graphics()
      scanlines.fillStyle(0x000000, 0.12)
      for (let row = 1; row < GAME_HEIGHT; row += 2) {
        scanlines.fillRect(0, row, GAME_WIDTH, 1)
      }
      scanlines.setDepth(200).setScrollFactor(0)
      this.bgScanlines = scanlines
    }

    // ── Animated Noise Atmosphere ───────────────────────────────
    this.bgNoiseOverlay = applyNoiseAtmosphere(
      this,
      GAME_WIDTH / 2,
      GAME_HEIGHT / 2,
      GAME_WIDTH,
      GAME_HEIGHT,
      0.05,
      1.0,
    )
    this.bgNoiseOverlay?.setDepth(-6)

    // ── Sector-specific animated overlays ───────────────────
    this.sectorOverlay = createSectorOverlay(
      this,
      sectorBackground.overlayType,
      0,
      0,
      GAME_WIDTH,
      GAME_HEIGHT,
    )
    this.sectorOverlay?.setDepth(60).setScrollFactor(0)
  }

  private loadSectorBackground(sectorId: string): void {
    if (this.loadingSectorBackgroundId === sectorId) return
    this.loadingSectorBackgroundId = sectorId

    const queued = queueSectorBackgroundAssets(this, sectorId)
    if (!queued) {
      this.loadingSectorBackgroundId = ""
      this.rebuildSectorBackground(sectorId)
      return
    }

    this.load.once("complete", () => {
      this.loadingSectorBackgroundId = ""
      this.rebuildSectorBackground(sectorId)
    })
    this.load.start()
  }

  private createArenaWalls(): void {
    const pad = SCENE_TUNING.bouncingBallBoundsPadding
    const gfx = this.add.graphics()
    gfx.setScrollFactor(0).setDepth(62).setBlendMode(Phaser.BlendModes.ADD)

    // ── Top rail — electric cyan ─────────────────────────────────
    gfx.lineStyle(14, NEON.cyan, 0.05)
    gfx.lineBetween(0, pad, GAME_WIDTH, pad)
    gfx.lineStyle(5, NEON.cyan, 0.22)
    gfx.lineBetween(0, pad, GAME_WIDTH, pad)
    gfx.lineStyle(1, NEON.cyan, 0.8)
    gfx.lineBetween(0, pad, GAME_WIDTH, pad)

    // ── Bottom rail — electric cyan ──────────────────────────────
    gfx.lineStyle(14, NEON.cyan, 0.05)
    gfx.lineBetween(0, GAME_HEIGHT - pad, GAME_WIDTH, GAME_HEIGHT - pad)
    gfx.lineStyle(5, NEON.cyan, 0.22)
    gfx.lineBetween(0, GAME_HEIGHT - pad, GAME_WIDTH, GAME_HEIGHT - pad)
    gfx.lineStyle(1, NEON.cyan, 0.8)
    gfx.lineBetween(0, GAME_HEIGHT - pad, GAME_WIDTH, GAME_HEIGHT - pad)

    // ── Left kickback wall — hot pink (gives the ball a speed boost) ─
    gfx.lineStyle(16, NEON.pink, 0.04)
    gfx.lineBetween(pad, 0, pad, GAME_HEIGHT)
    gfx.lineStyle(6, NEON.pink, 0.18)
    gfx.lineBetween(pad, 0, pad, GAME_HEIGHT)
    gfx.lineStyle(1, NEON.pink, 0.7)
    gfx.lineBetween(pad, 0, pad, GAME_HEIGHT)

    // ── Right boundary wall — cyan, to complete the arena frame ─
    gfx.lineStyle(14, NEON.cyan, 0.04)
    gfx.lineBetween(GAME_WIDTH - pad, 0, GAME_WIDTH - pad, GAME_HEIGHT)
    gfx.lineStyle(5, NEON.cyan, 0.18)
    gfx.lineBetween(GAME_WIDTH - pad, 0, GAME_WIDTH - pad, GAME_HEIGHT)
    gfx.lineStyle(1, NEON.cyan, 0.68)
    gfx.lineBetween(GAME_WIDTH - pad, 0, GAME_WIDTH - pad, GAME_HEIGHT)

    // ── Corner bracket accents ───────────────────────────────────
    const ARM = 36
    const corners = [
      { x: pad, y: pad }, // top-left
      { x: pad, y: GAME_HEIGHT - pad }, // bottom-left
      { x: GAME_WIDTH - pad, y: pad }, // top-right
      { x: GAME_WIDTH - pad, y: GAME_HEIGHT - pad }, // bottom-right
    ]

    gfx.lineStyle(2, NEON.cyan, 0.85)
    for (const c of corners) {
      const sx = c.x === pad ? 1 : -1
      const sy = c.y === pad ? 1 : -1
      gfx.lineBetween(c.x, c.y, c.x + sx * ARM, c.y)
      gfx.lineBetween(c.x, c.y, c.x, c.y + sy * ARM)
    }

    // Corner dots
    gfx.fillStyle(NEON.cyan, 0.9)
    for (const c of corners) {
      gfx.fillCircle(c.x, c.y, 2.5)
    }

    // Left-wall corner dots in pink (matching the kickback wall colour)
    gfx.fillStyle(NEON.pink, 0.85)
    gfx.fillCircle(pad, pad, 3)
    gfx.fillCircle(pad, GAME_HEIGHT - pad, 3)
  }

  private isOverdriveActive(): boolean {
    return this.gameData.player.powerMode === "overdrive"
  }

  private setupInputHandlers(): void {
    this.inputManager = new InputManager(this, {
      onDown: (action, event) => this.onInputDown(action, event),
      onUp: (action, event) => this.onInputUp(action, event),
    })
  }

  private onInputDown(action: InputAction, event?: WaveInputEvent): void {
    if (this.gameData.state !== "playing") return

    switch (action) {
      case "FLAP": {
        // Don't flap if tapping a UI button in the HUD
        if (event?.currentlyOver?.some((obj) => obj.getData("noFlap"))) return

        // Track press time for hold-to-shoot
        if (event instanceof KeyboardEvent) {
          this.spacePressedAt = this.time.now
        } else if (!event?.pointer) {
          this.lastGamepadDownAt = this.time.now
        }

        if (this.isOverdriveActive()) {
          this.shoot()
          this.clearChargeVisuals()
          this.isAiming = false
        } else {
          this.isAiming = true
          this.chargeStartTime = this.time.now
        }
        this.flap()
        break
      }
      case "PAUSE":
        this.requestPause()
        break
      case "LEFT":
      case "RIGHT":
      case "UP":
      case "DOWN":
        this.setKeyboardAimAction(action, true)
        break
    }
  }

  private onInputUp(action: InputAction, event?: WaveInputEvent): void {
    if (this.gameData.state !== "playing") return

    switch (action) {
      case "FLAP": {
        if (!this.isOverdriveActive()) {
          let duration = 0
          if (event?.pointer) {
            duration = event.pointer.upTime - event.pointer.downTime
          } else if (this.spacePressedAt > 0) {
            duration = this.time.now - this.spacePressedAt
            this.spacePressedAt = 0
          } else {
            duration = this.time.now - this.lastGamepadDownAt
          }

          if (this.isAiming && duration >= WaveScene.FLAP_SHOOT_THRESHOLD_MS) {
            this.shoot()
          }
        }
        this.clearChargeVisuals()
        this.isAiming = false
        break
      }
      case "LEFT":
      case "RIGHT":
      case "UP":
      case "DOWN":
        this.setKeyboardAimAction(action, false)
        break
    }
  }

  private setupCollisions(): void {
    this.physics.add.overlap(this.dogSprite, this.enemyGroup, (_dog, enemy) =>
      this.collisions.onDogEnemyCollision(enemy as GameSprite),
    )
    this.physics.add.overlap(
      this.dogSprite,
      this.projectileGroup,
      (_dog, proj) =>
        this.collisions.onDogProjectileCollision(proj as GameSprite),
    )
    this.physics.add.overlap(this.dogSprite, this.expCoinGroup, (_dog, coin) =>
      this.collisions.onDogCoinCollision(coin as GameSprite),
    )

    // Bumpers block enemy projectiles
    this.physics.add.overlap(
      this.bumperGroup,
      this.projectileGroup,
      (_bumper, proj) =>
        this.collisions.onBumperProjectileCollision(proj as GameSprite),
    )

    this.setupCombatBallCollisions()
  }

  // ══════════════════════════════════════════════════════════════
  //  ACTIONS
  // ══════════════════════════════════════════════════════════════

  private flap(): void {
    if (this.gameData.state !== "playing") return
    this.playSfx(AUDIO.SFX_THRUSTER, 0.6)

    const isOverdrive = this.gameData.player.powerMode === "overdrive"
    const flapForce =
      FLAP_POWER *
      (this.character.flapPower || 1) *
      (this.gameData.player.stats.flapPower || 1) *
      SCENE_TUNING.flapForceScale *
      (isOverdrive ? 0.18 : 1)
    const body = this.dogSprite.body as Phaser.Physics.Arcade.Body
    body.setVelocityY(
      Math.min(
        body.velocity.y *
          (isOverdrive ? 0.25 : SCENE_TUNING.flapVelocityCarryFactor),
        0,
      ) + flapForce,
    )
    if (!isOverdrive) {
      body.setVelocityX(
        Phaser.Math.Clamp(
          body.velocity.x + SCENE_TUNING.flapHorizontalBoost,
          SCENE_TUNING.playerHorizontalVelocityMin,
          SCENE_TUNING.playerHorizontalVelocityMax,
        ),
      )
    }
    this.vfx.sparkEmitter.explode(
      SCENE_TUNING.flapSparkCount,
      this.dogSprite.x - SCENE_TUNING.flapSparkOffsetX,
      this.dogSprite.y + SCENE_TUNING.flapSparkOffsetY,
    )

    // Jetpack flame burst on flap — big satisfying thrust at nozzle
    const flapRad = Phaser.Math.DegToRad(this.dogSprite.angle)
    const nozzleFlapX =
      this.dogSprite.x + Math.cos(flapRad) * -38 - Math.sin(flapRad) * 4
    const nozzleFlapY =
      this.dogSprite.y + Math.sin(flapRad) * -38 + Math.cos(flapRad) * 4
    this.vfx.emitJetpackFlameBurst(
      isOverdrive ? 3 : 10,
      nozzleFlapX,
      nozzleFlapY,
    )

    this.applySquashAndStretch(
      isOverdrive ? 0.95 : 0.85,
      isOverdrive ? 1.05 : 1.2,
      isOverdrive ? 80 : 120,
    )
  }

  private shoot(): void {
    if (this.gameData.state !== "playing") return
    const chargeLevel = this.getChargeLevel()
    const body = this.dogSprite.body as Phaser.Physics.Arcade.Body
    const pointer = this.input.activePointer
    const isOverdrive = this.isOverdriveActive()
    const aimAngle = this.getCurrentAimAngleDeg(pointer)
    const system = this.combatBallSystem
    if (!system) return
    const launched = system.tryLaunchOnFlap(
      this.dogSprite.x,
      this.dogSprite.y,
      body.velocity.x,
      body.velocity.y,
      aimAngle,
      pointer ? { x: pointer.x, y: pointer.y } : null,
      isOverdrive,
      chargeLevel,
    )
    if (launched) {
      this.sfx.playLauncherShot()
      if (chargeLevel > 0 && !isOverdrive) {
        this.vfx.sparkEmitter.explode(
          chargeLevel * 8,
          this.dogSprite.x,
          this.dogSprite.y,
        )
      }
      if (isOverdrive) {
        const angleRad = Phaser.Math.DegToRad(aimAngle)
        this.vfx.emitOverdriveFire(
          this.dogSprite.x + Math.cos(angleRad) * 44,
          this.dogSprite.y + Math.sin(angleRad) * 44,
          angleRad,
        )
        this.applySquashAndStretch(1.15, 0.85, 80)
      }
    }
  }

  private getChargeLevel(): 0 | 1 | 2 {
    if (!this.isAiming || this.isOverdriveActive()) return 0
    const elapsed = this.time.now - this.chargeStartTime
    if (elapsed >= WaveScene.CHARGE_LEVEL2_MS) return 2
    if (elapsed >= WaveScene.CHARGE_LEVEL1_MS) return 1
    return 0
  }

  private updateChargeVisuals(): void {
    this.chargeOrbGraphics.clear()
    if (!this.isAiming || this.isOverdriveActive()) return

    const elapsed = this.time.now - this.chargeStartTime
    if (elapsed < WaveScene.CHARGE_LEVEL1_MS) return

    const t = this.time.now
    const cx = this.dogSprite.x
    const cy = this.dogSprite.y

    const spriteRad = Phaser.Math.DegToRad(this.dogSprite.angle)
    const cosR = Math.cos(spriteRad)
    const sinR = Math.sin(spriteRad)
    const mx = cx + cosR * 36
    const my = cy + sinR * 22

    // Play charge sound when crossing each level threshold
    const currentLevel = elapsed >= WaveScene.CHARGE_LEVEL2_MS ? 2 : 1
    if (currentLevel > this.chargeLastSoundLevel) {
      this.playSfx(AUDIO.SFX_HADOUKEN_CHARGE, 0.8)
      this.chargeLastSoundLevel = currentLevel
    }

    const progress = Phaser.Math.Clamp(
      (elapsed - WaveScene.CHARGE_LEVEL1_MS) /
        (WaveScene.CHARGE_LEVEL2_MS - WaveScene.CHARGE_LEVEL1_MS),
      0,
      1,
    )
    const pulse = 0.7 + Math.sin(t / 70) * 0.3
    const fastPulse = 0.8 + Math.sin(t / 40) * 0.2
    const baseRadius = Phaser.Math.Linear(10, 26, progress)

    if (elapsed >= WaveScene.CHARGE_LEVEL2_MS) {
      // Full charge — cyan + magenta rings matching overdrive energy
      this.chargeOrbGraphics.lineStyle(3, NEON.magenta, 0.6 * pulse)
      this.chargeOrbGraphics.strokeCircle(
        mx,
        my,
        baseRadius * 1.3 + Math.sin(t / 90) * 3,
      )
      this.chargeOrbGraphics.lineStyle(1.5, 0xffffff, 0.85 * fastPulse)
      this.chargeOrbGraphics.strokeCircle(
        mx,
        my,
        baseRadius * 0.55 + Math.sin(t / 70) * 2,
      )

      if (t - this.lastChargeSparkTime >= 120) {
        this.vfx.sparkEmitter.explode(4, mx, my)
        this.lastChargeSparkTime = t
      }
    } else {
      // Growing cyan ring
      this.chargeOrbGraphics.lineStyle(2, NEON.cyan, 0.65 * pulse)
      this.chargeOrbGraphics.strokeCircle(
        mx,
        my,
        baseRadius + Math.sin(t / 90) * 2,
      )
      this.chargeOrbGraphics.lineStyle(1, 0xffffff, 0.7 * fastPulse)
      this.chargeOrbGraphics.strokeCircle(
        mx,
        my,
        baseRadius * 0.5 + Math.sin(t / 70) * 1.5,
      )
    }
  }

  private clearChargeVisuals(): void {
    this.chargeOrbGraphics.clear()
    this.chargeLastSoundLevel = 0
  }

  public handlePlayerDamage(
    damage: number,
    _source: "enemy" | "projectile" | "floor" | "generic" = "generic",
  ): void {
    if (this.waveObserver.enabled) return
    if (this.debugInvincible) return
    if (
      this.gameData.player.isInvulnerable ||
      this.gameData.player.powerMode === "overdrive"
    )
      return

    // Track damage source for revive logic
    this.lastDamageSource = _source

    // Wave-guard charges absorb a hit
    if (this.gameData.player.waveGuardCharges > 0) {
      this.gameData.player.waveGuardCharges -= 1
      this.vfx.flashScreen(
        COLOR.success,
        SCENE_TUNING.waveGuardFlashAlpha,
        SCENE_TUNING.waveGuardFlashDurationMs,
      )
      this.vfx.pickupEmitter.explode(
        SCENE_TUNING.bumperTreatBurstParticles,
        this.dogSprite.x,
        this.dogSprite.y,
      )
      return
    }

    this.gameData.player.hp -= damage
    this.vfx.showDamageNumber(
      this.dogSprite.x,
      this.dogSprite.y - 20,
      damage,
      COLOR.danger,
    )
    this.playSfx(AUDIO.DAMAGE, 0.4)
    this.gameData.player.isInvulnerable = true
    this.gameData.player.invulnerabilityTimer = INVULNERABILITY_TIME
    this.dogSprite.setTint(COLOR.dangerBright)
    this.vfx.shakeCamera(250, 0.02)
    this.vfx.flashScreen(COLOR.danger, 0.35, 120)
    this.vfx.heatHazePulse(0.012, 650)

    // Scale bump
    this.dogSprite.setDisplaySize(
      SPRITE_SIZES.player_sprite.w * 1.2,
      SPRITE_SIZES.player_sprite.h * 1.2,
    )
    this.time.delayedCall(100, () => {
      if (this.dogSprite.active) {
        this.dogSprite.setDisplaySize(
          SPRITE_SIZES.player_sprite.w,
          SPRITE_SIZES.player_sprite.h,
        )
      }
    })

    this.gameData.player.scoreMultiplier = 1

    EventBus.emit(EVENT.PLAYER_DAMAGED, damage)
  }

  public handlePlayerHeal(amount: number): void {
    if (this.gameData.player.hp >= this.gameData.player.maxHp) return

    this.gameData.player.hp = Math.min(
      this.gameData.player.maxHp,
      this.gameData.player.hp + amount,
    )

    this.vfx.showDamageNumber(
      this.dogSprite.x,
      this.dogSprite.y - 20,
      amount,
      COLOR.success,
      false, // Is not damage
    )
  }

  private collectCoin(value: number, x: number, y: number): void {
    const now = this.time.now
    if (now - this.lastPickupSfxAt >= WaveScene.PICKUP_SFX_COOLDOWN_MS) {
      this.playSfx(AUDIO.PICKUP, 0.25)
      this.lastPickupSfxAt = now
    }
    const duplicated = this.economy.collectCoin(value)
    if (duplicated) {
      this.vfx.pickupEmitter.explode(
        SCENE_TUNING.duplicateTreatBurstParticles,
        x,
        y,
      )
    }
  }

  private getProjectileEffectColor(
    pd: Extract<GameSprite["entityData"], { kind: "projectile" }>,
  ): number {
    if (pd.isEnemy) return COLOR.danger

    return Phaser.Display.Color.HexStringToColor(pd.color).color
  }

  // ══════════════════════════════════════════════════════════════
  //  UPDATE LOOPS
  // ══════════════════════════════════════════════════════════════

  private updatePlayer(_time: number, delta: number): void {
    this.playerRuntime.update(delta)
  }

  private destroyThreatsTouchingPlayer(): void {
    for (const child of this.enemyGroup.children) {
      const enemy = child as GameSprite
      if (!enemy.active) continue
      if (
        Phaser.Math.Distance.Between(
          enemy.x,
          enemy.y,
          this.dogSprite.x,
          this.dogSprite.y,
        ) >= FIREBALL_AURA_RADIUS
      ) {
        continue
      }

      const ed = enemy.entityData
      if (!ed || ed.kind !== "enemy") continue

      if (ed.bossMaxHp !== undefined || ed.bossWave !== undefined) {
        this.damageBossEnemy(enemy, FIREBALL_BOSS_DAMAGE_RATIO, COLOR.magenta)
      } else {
        this.enemyFlow.defeatEnemy(enemy)
      }
    }
  }

  private getCurrentAimAngleDeg(
    pointer: Phaser.Input.Pointer | null = this.input.activePointer,
  ): number {
    // Controller/Keyboard aim takes absolute priority if present
    if (this.controllerAimAngleDeg !== null) {
      return this.controllerAimAngleDeg
    }

    // Fallback to mouse pointer ONLY if it was recently moved or is active
    if (
      this.gameData.player.powerMode === "overdrive" &&
      pointer &&
      pointer.active
    ) {
      return Phaser.Math.RadToDeg(
        Math.atan2(pointer.y - this.dogSprite.y, pointer.x - this.dogSprite.x),
      )
    }

    return this.dogSprite.angle
  }

  private updateDirectionalAim(): void {
    if (!this.isOverdriveActive()) {
      this.controllerAimAngleDeg = null
      return
    }

    const pad = this.getPrimaryGamepad()
    const controllerAim = pad ? this.getPadAimAngleDeg(pad) : null
    const keyboardAim = this.getKeyboardAimAngleDeg()

    if (controllerAim !== null) {
      this.controllerAimAngleDeg = controllerAim
      return
    }

    if (keyboardAim !== null) {
      this.controllerAimAngleDeg = keyboardAim
      return
    }

    // During overdrive, we DON'T reset to null if we already have a value.
    // This allows the player to "set" an angle and keep it.
    if (!this.isAiming && this.controllerAimAngleDeg === null) {
      this.controllerAimAngleDeg = null
    }
  }

  private getPrimaryGamepad(): Phaser.Input.Gamepad.Gamepad | null {
    const gamepad = this.input.gamepad
    return (
      gamepad?.pad1 ?? gamepad?.pad2 ?? gamepad?.pad3 ?? gamepad?.pad4 ?? null
    )
  }

  private getPadAimAngleDeg(pad: Phaser.Input.Gamepad.Gamepad): number | null {
    const deadzone = 0.25

    const dpadX = (pad.right ? 1 : 0) - (pad.left ? 1 : 0)
    const dpadY = (pad.down ? 1 : 0) - (pad.up ? 1 : 0)
    if (dpadX !== 0 || dpadY !== 0) {
      // In screen coordinates, positive Y is down.
      return Phaser.Math.RadToDeg(Math.atan2(dpadY, dpadX))
    }

    const rightX = pad.rightStick.x
    const rightY = pad.rightStick.y
    if (Math.abs(rightX) > deadzone || Math.abs(rightY) > deadzone) {
      return Phaser.Math.RadToDeg(Math.atan2(rightY, rightX))
    }

    const leftX = pad.leftStick.x
    const leftY = pad.leftStick.y
    if (Math.abs(leftX) > deadzone || Math.abs(leftY) > deadzone) {
      return Phaser.Math.RadToDeg(Math.atan2(leftY, leftX))
    }

    return null
  }

  private getKeyboardAimAngleDeg(): number | null {
    const x = (this.keyboardAimRight ? 1 : 0) - (this.keyboardAimLeft ? 1 : 0)
    const y = (this.keyboardAimDown ? 1 : 0) - (this.keyboardAimUp ? 1 : 0)

    if (x === 0 && y === 0) return null

    return Phaser.Math.RadToDeg(Math.atan2(y, x))
  }

  private setKeyboardAimAction(action: InputAction, pressed: boolean): void {
    switch (action) {
      case "LEFT":
        this.keyboardAimLeft = pressed
        break
      case "RIGHT":
        this.keyboardAimRight = pressed
        break
      case "UP":
        this.keyboardAimUp = pressed
        break
      case "DOWN":
        this.keyboardAimDown = pressed
        break
    }
  }

  private updateEnemies(time: number, delta: number): void {
    this.enemyFlow.updateEnemies(time, delta)
  }

  private updateProjectiles(_time: number, delta: number): void {
    this.field.updateProjectiles(delta)
  }

  private updateSpawning(delta: number): void {
    this.enemySpawnTimer += delta
    const waveNumber = this.getActiveWaveNumber()
    const difficultyMultiplier = this.getSpawnDifficultyMultiplier(waveNumber)
    const waveProgress = this.waveTimeline.getWaveProgress()
    const spawnInterval = this.waveTimeline.getSpawnIntervalMs()

    if (this.waveTimeline.isBossWave()) {
      // Skip regular enemy spawning on boss waves, but still spawn bumpers/slingshots
      this.updateBumperSpawning(delta)
      return
    }

    if (this.enemySpawnTimer >= spawnInterval) {
      this.enemySpawnTimer -= spawnInterval
      const activeCount = this.enemyGroup.countActive(true)
      const cap = this.getEnemyPopulationCap()
      const slotsAvailable = Math.max(0, cap - activeCount)

      if (slotsAvailable <= 0) return

      this._spawnCycleCount = (this._spawnCycleCount ?? 0) + 1
      if (
        this.waveTimeline.shouldUseEncounter(this._spawnCycleCount) &&
        slotsAvailable >= 2
      ) {
        const group = this.waveTimeline.getEncounterGroup(slotsAvailable)
        if (group && group.length > 0) {
          const toSpawn = group.slice(
            0,
            this.waveTimeline.getEncounterSize(slotsAvailable),
          )
          const spawnPoints = waveStrategy.getSpawnPoints(
            toSpawn.length,
            waveNumber,
            waveProgress,
          )
          let usedSlots = 0
          for (const [index, enemyType] of toSpawn.entries()) {
            const remainingSlots = slotsAvailable - usedSlots
            if (remainingSlots <= 0) break

            const spawnPoint = spawnPoints[index]
            usedSlots += spawnWaveEnemyEntry({
              enemyType,
              baseX: spawnPoint.x,
              baseY: spawnPoint.y,
              remainingSlots,
              difficultyMultiplier,
              waveNumber,
              source: "encounter",
              createEnemyByType: this.enemies.createEnemyByType.bind(
                this.enemies,
              ),
              spawnConfiguredEnemy: this.enemies.spawnConfiguredEnemy.bind(
                this.enemies,
              ),
              spawnStandardEnemy: this.spawnEnemyInternal.bind(this),
              observeSpawn: (sprite, observedSource) =>
                this.observer.observeSpawn(sprite, observedSource),
            })
          }
        } else {
          const enemyType = this.waveTimeline.rollEnemyType()
          const spawnPoint = waveStrategy.getSpawnPoints(
            1,
            waveNumber,
            waveProgress,
          )[0]
          spawnWaveEnemyEntry({
            enemyType,
            baseX: spawnPoint.x,
            baseY: spawnPoint.y,
            remainingSlots: slotsAvailable,
            difficultyMultiplier,
            waveNumber,
            source: "fallback",
            createEnemyByType: this.enemies.createEnemyByType.bind(
              this.enemies,
            ),
            spawnConfiguredEnemy: this.enemies.spawnConfiguredEnemy.bind(
              this.enemies,
            ),
            spawnStandardEnemy: this.spawnEnemyInternal.bind(this),
            observeSpawn: (sprite, observedSource) =>
              this.observer.observeSpawn(sprite, observedSource),
          })
        }
      } else {
        const batchSize = Math.min(
          this.waveTimeline.getSpawnBatchSize(),
          slotsAvailable,
        )
        const spawnPoints = waveStrategy.getSpawnPoints(
          batchSize,
          waveNumber,
          waveProgress,
        )
        let usedSlots = 0
        for (let i = 0; i < batchSize; i++) {
          const enemyType = this.waveTimeline.rollEnemyType()
          const remainingSlots = slotsAvailable - usedSlots
          if (remainingSlots <= 0) break

          const spawnPoint = spawnPoints[i]
          usedSlots += spawnWaveEnemyEntry({
            enemyType,
            baseX: spawnPoint.x,
            baseY: spawnPoint.y,
            remainingSlots,
            difficultyMultiplier,
            waveNumber,
            source: "batch",
            createEnemyByType: this.enemies.createEnemyByType.bind(
              this.enemies,
            ),
            spawnConfiguredEnemy: this.enemies.spawnConfiguredEnemy.bind(
              this.enemies,
            ),
            spawnStandardEnemy: this.spawnEnemyInternal.bind(this),
            observeSpawn: (sprite, observedSource) =>
              this.observer.observeSpawn(sprite, observedSource),
          })
        }
      }
    }

    this.updateBumperSpawning(delta)
  }

  private updateBumperSpawning(delta: number): void {
    const waveNumber = this.getActiveWaveNumber()
    this.bumperSpawnTimer += delta
    const effectiveBumperInterval =
      waveStrategy.getBumperSpawnIntervalMs(waveNumber)
    if (this.bumperSpawnTimer >= effectiveBumperInterval) {
      this.bumperSpawnTimer = 0
      const mergedWeights = waveStrategy.getBumperTypeWeights(waveNumber)
      this.bumperSystem.spawnBumper(mergedWeights)
    }

    // Slingshot deflectors are utility launchers and can appear from wave 1.
    if (
      waveNumber >= BUMPER_PROGRESSION.slingshotWave &&
      this.combatBallSystem
    ) {
      this.slingshotSpawnTimer += delta
      if (
        this.slingshotSpawnTimer >=
        waveStrategy.getSlingshotSpawnIntervalMs(waveNumber)
      ) {
        this.slingshotSpawnTimer = 0
        this.bumperSystem.spawnSlingshot(waveNumber)
      }
    }
  }

  private updateWave(delta: number): void {
    const effectiveDelta = this._infiniteTime ? 0 : delta
    const waveChanged = this.waveTimeline.update(effectiveDelta)
    this.syncWaveMetadata()

    if (waveChanged) {
      this.startWaveEndSummary()
    }
  }

  private getValidEnemyType(enemyType: EnemyType): EnemyType | null {
    if (enemyType === "router" && this.enemies.hasActiveRouter()) {
      const alternatives: EnemyType[] = [
        "gunPod",
        "orbDrone",
        "kamikaze",
        "chainDrifter",
      ]
      const rolled = this.waveTimeline.rollEnemyType()
      return alternatives.includes(rolled)
        ? rolled
        : alternatives[Math.floor(Math.random() * alternatives.length)]
    }
    return enemyType
  }

  private spawnEnemyInternal(
    enemyType: EnemyType,
    x: number,
    y: number,
    difficultyMultiplier: number,
    source: "encounter" | "fallback" | "batch",
  ): number {
    const waveNumber = this.getActiveWaveNumber()
    const validType = this.getValidEnemyType(enemyType)
    if (!validType) {
      return 0
    }

    let targetY = y
    if (validType === "router") {
      const lanes = [GAME_HEIGHT * 0.24, GAME_HEIGHT * 0.48, GAME_HEIGHT * 0.74]
      targetY = Math.random() < 0.5 ? lanes[0] : lanes[2]
    }

    const enemy = this.enemies.createEnemyByType(
      validType,
      x,
      targetY,
      waveNumber,
      difficultyMultiplier,
    )
    const spawnedEnemy = this.enemies.spawnEnemy(enemy)
    if (spawnedEnemy) {
      this.observer.observeSpawn(spawnedEnemy, source)
      return 1
    }

    return 0
  }

  private updateExpCoins(delta: number): void {
    this.field.updateExpCoins(delta, this.waveTimeline.getWaveProgress())
  }

  private updateBumpers(time: number, delta: number): void {
    this.field.updateBumpers(time, delta)
  }

  private checkBumperCollisions(): void {
    this.field.checkBumperCollisions()
  }

  private updateParallax(delta: number): void {
    const sectorBackground = getSectorBackground(
      this.waveTimeline.getCurrentSector().id,
    )
    this.bgLayers.forEach((layer, i) => {
      const layerDef = sectorBackground.layers[i]
      const parallaxSpeed =
        layerDef?.parallaxSpeed ?? SCENE_TUNING.parallaxSpeeds[i]
      const autoscrollX = (layerDef?.autoscrollX ?? 0) * (delta / 16)
      const autoscrollY = (layerDef?.autoscrollY ?? 0) * (delta / 16)
      const normalizedTileScaleX = layer.tileScaleX || 1
      const normalizedTileScaleY = layer.tileScaleY || 1

      // Horizontal parallax + autoscroll
      layer.tilePositionX +=
        (this.gameData.scrollSpeed * parallaxSpeed * (delta / 16) +
          autoscrollX) /
        normalizedTileScaleX

      // Vertical shift (if any) + autoscroll
      // We don't have a vertical camera yet, but we can simulate it or just use autoscroll
      layer.tilePositionY += autoscrollY / normalizedTileScaleY

      layer.x = GAME_WIDTH / 2
      layer.y = this.bgLayerBaseY[i]
    })

    if (this.bgNoiseOverlay) {
      this.bgNoiseOverlay.noiseOffset[0] += 0.0025 * (delta / 16)
      this.bgNoiseOverlay.noiseOffset[1] += 0.0012 * (delta / 16)
      this.bgNoiseOverlay.noiseFlow += 0.0015 * (delta / 16)
      this.bgNoiseOverlay.x = GAME_WIDTH / 2
      this.bgNoiseOverlay.y = GAME_HEIGHT / 2
    }

    if (this.bgDispNoise) {
      this.bgDispNoise.noiseOffset[0] += 0.0007 * (delta / 16)
      this.bgDispNoise.noiseOffset[1] += 0.0005 * (delta / 16)
      this.bgDispNoise.noiseFlow += 0.0004 * (delta / 16)
    }

    if (this.bgCellNoise) {
      // 4D cellular noise: x scrolls with scene speed, z/w evolve for organic ripple
      const scrollFrac = (this.gameData.scrollSpeed * (delta / 16)) / GAME_WIDTH
      this.bgCellNoise.noiseOffset[0] -= scrollFrac * 6
      this.bgCellNoise.noiseOffset[2] = Math.cos(
        this.bgCellNoise.noiseOffset[0] / 10,
      )
      this.bgCellNoise.noiseOffset[3] = Math.sin(
        this.bgCellNoise.noiseOffset[0] / 10,
      )

      // Keep the river reflection scrolling in lockstep with the buildings (layer-1)
      if (this.bgRiverReflection && this.bgLayers[1]) {
        this.bgRiverReflection.tilePositionX = this.bgLayers[1].tilePositionX
      }

      // City window lights: shift container so lights move with the building texture
      if (
        this.bgCityLights &&
        this.bgLayers[1] &&
        this.bgCityLightsRepeatPx > 0
      ) {
        const scrolled =
          this.bgLayers[1].tilePositionX * this.bgLayers[1].tileScaleX
        this.bgCityLights.x = -(scrolled % this.bgCityLightsRepeatPx)
      }
    }

    if (this.sectorOverlay) {
      this.sectorOverlay.x = 0
      this.sectorOverlay.y = 0
    }
  }

  // ══════════════════════════════════════════════════════════════
  //  WAVE END / SHOP / GAME STATE
  // ══════════════════════════════════════════════════════════════

  private startWaveEndSummary(): void {
    this.run.startWaveEndSummary()
  }

  /** Called by ShopScene when "CONTINUE" is pressed */
  public resumeAfterShop(): void {
    this.run.resumeAfterShop()
  }

  private checkGameState(): void {
    // Victory
    if (this.waveTimeline.isGameWon()) {
      this.handleVictory()
      return
    }
    // Defeat - try revive first
    if (this.gameData.player.hp <= 0) {
      const isBossDeath = this.lastDamageSource === "enemy"
      const revived = tryRevivePlayer(this.gameData.player, isBossDeath)
      if (revived) {
        // Reset invulnerability and show VFX
        this.gameData.player.isInvulnerable = false
        this.gameData.player.invulnerabilityTimer = 0
        this.dogSprite.clearTint()
        this.vfx.flashScreen(COLOR.success, 0.5, 300)
        this.vfx.pickupEmitter.explode(20, this.dogSprite.x, this.dogSprite.y)
        return
      }
      this.handleDefeat()
    }
  }

  private handleVictory(): void {
    if (this.isRunEnding) return
    this.isRunEnding = true

    if (this.observer.enabled) {
      this.observer.restart("victory")
      return
    }

    this.playSfx(AUDIO.SFX_VICTORY, 1.1)
    this.setRunStage("victory")
    this.physics.pause()
    this.scene.stop(SCENE.HUD)
    this.scene.start(SCENE.GAME_OVER, {
      isVictory: true,
      score: this.gameData.score,
      wave: this.gameData.wave,
      coins: this.gameData.coins,
      totalBumperHits: this.gameData.totalBumperHits,
      player: this.gameData.player,
    })
    EventBus.emit(EVENT.GAME_VICTORY)
  }

  private handleDefeat(): void {
    if (this.isRunEnding) return
    this.isRunEnding = true

    if (this.observer.enabled) {
      this.observer.restart("defeat")
      return
    }

    this.playSfx(AUDIO.SFX_GAME_OVER, 1.1)
    this.setRunStage("gameOver")
    this.cameras.main.shake(500, 0.02)
    this.physics.pause()
    this.scene.stop(SCENE.HUD)
    this.scene.start(SCENE.GAME_OVER, {
      isVictory: false,
      score: this.gameData.score,
      wave: this.gameData.wave,
      coins: this.gameData.coins,
      totalBumperHits: this.gameData.totalBumperHits,
      player: this.gameData.player,
    })
    EventBus.emit(EVENT.GAME_OVER)
  }

  public requestPause(): void {
    this.run.requestPause()
  }

  /** Called by PauseScene on resume */
  public resumeGame(): void {
    this.run.resumeGame()
  }

  // ══════════════════════════════════════════════════════════════
  //  UTILITY HELPERS
  // ══════════════════════════════════════════════════════════════

  private spawnCoin(x: number, y: number, value: number): void {
    // Apply Green Fields coin value multiplier
    let adjustedValue = value
    if (this.sectorModifier?.id === "fields_greed") {
      adjustedValue = Math.floor(
        value * (this.sectorModifier.params.coinValueMultiplier ?? 1),
      )
    }
    const coinData = createExpCoin(x, y, adjustedValue)
    const tex =
      adjustedValue >= 9
        ? TEX.COIN_L
        : adjustedValue >= 4
          ? TEX.COIN_M
          : TEX.COIN_S
    const anim =
      adjustedValue >= 9
        ? "anim_coin_l"
        : adjustedValue >= 4
          ? "anim_coin_m"
          : "anim_coin_s"
    const coinSprite = this.physics.add.sprite(
      coinData.x,
      coinData.y,
      tex,
    ) as GameSprite
    coinSprite.play(anim)
    coinSprite.setDisplaySize(coinData.size * 1.5, coinData.size * 1.5)
    coinSprite.entityData = {
      kind: "coin",
      id: coinData.id,
      value: coinData.value,
      size: coinData.size,
      x: coinData.x,
      y: coinData.y,
    }
    coinSprite.setData("id", coinData.id)
    const body = coinSprite.body as Phaser.Physics.Arcade.Body
    if (body) body.setAllowGravity(false)
    this.expCoinGroup.add(coinSprite)
  }

  private getEnemyPopulationCap(): number {
    return this.waveTimeline.getEnemyPopulationCap()
  }

  private spawnBossEncounter(): void {
    const waveNumber = this.getActiveWaveNumber()
    this.bossSpawned = true
    const sector = this.waveTimeline.getCurrentSector()
    const bossDef = sector.boss
    const bossStats = resolveBossEncounterStats(bossDef)

    const spawnX = GAME_WIDTH + 400
    const holdX = Math.round(GAME_WIDTH * 0.72)
    const spawnY = GAME_HEIGHT * 0.5

    this.enemies.spawnConfiguredEnemy(
      bossDef.type,
      spawnX,
      spawnY,
      {
        vx: 0,
        vy: 0,
        hp: bossStats.hp,
        damage: bossStats.damage,
        points: bossStats.points,
        shootCooldown: bossStats.shootCooldown,
        bossMaxHp: bossStats.hp,
        bossWave: waveNumber,
        homeX: holdX,
        homeY: spawnY,
      },
      {
        introScale: bossDef.introScale,
        fadeIn: false,
        tweenDurationMs: 0,
        depth: 80,
      },
    )
  }

  private isBossEnemy(enemy: GameSprite): boolean {
    const ed = enemy.entityData
    return ed?.kind === "enemy" && ed.bossMaxHp !== undefined
  }

  private damageBossEnemy(
    enemy: GameSprite,
    ratio: number,
    color: number,
  ): void {
    const ed = enemy.entityData
    if (!ed || ed.kind !== "enemy") return
    const damage = Math.max(1, Math.round(ratio * (ed.bossMaxHp ?? ed.hp)))
    ed.hp = Math.max(0, ed.hp - damage)
    this.vfx.emitImpactBurst(enemy.x, enemy.y, color, 1.2)
    this.vfx.showDamageNumber(enemy.x, enemy.y - 18, damage, color)
    if (ed.hp <= 0) {
      this.enemyFlow.defeatEnemy(enemy)
    }
  }

  private handleBossDefeated(): void {
    if (this.bossDefeatSummaryStarted) return
    this.bossDefeatSummaryStarted = true
    EventBus.emit(EVENT.BOSS_DEFEATED, {
      wave: this.getActiveWaveNumber(),
      sector: this.waveTimeline.getCurrentSector().id,
    })
    this.waveTimeline.completeBossWave()
    // Wave number stays the same for now to keep the background stable during summary.
    // Slowly phase out boss music and return to normal wave music.
    returnToWaveMusic(this, 1800)
    this.startWaveEndSummary()
  }

  private updateSectorModifiers(delta: number): void {
    this.sectorFx.update(delta)
  }

  private updateDarknessOverlay(alpha: number): void {
    if (!this.darknessOverlay) {
      this.darknessOverlay = this.add.graphics()
      this.darknessOverlay.setDepth(55).setScrollFactor(0)
    }
    this.darknessOverlay.clear()
    this.darknessOverlay.fillStyle(0x000000, alpha)
    this.darknessOverlay.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT)
  }

  private updateComboEscalationVisuals(): void {
    const player = this.gameData.player
    const combo = player.combo

    // Check if combo state or multiplier signature changed
    const signature = `${combo.state}_${player.scoreMultiplier.toFixed(1)}`
    if (signature !== this.lastComboEscalationSignature) {
      this.lastComboEscalationSignature = signature

      const isPowered =
        combo.state === "powered" || player.scoreMultiplier > 1.2
      if (isPowered) {
        this.vfx.applyComboEscalation(player.scoreMultiplier)
      } else {
        this.vfx.clearComboEscalation()
      }

      // Apply/remove Glow filter on all active balls to reflect combo state
      const glowStrength = isPowered
        ? Math.min(6, 2 + (player.scoreMultiplier - 1) * 4)
        : 0
      for (const child of this.combatBallGroup.getChildren()) {
        const ball = child as GameSprite
        if (!ball.active) continue
        ball.filters?.internal.clear()
        ball.setBlendMode(
          glowStrength > 0 ? Phaser.BlendModes.ADD : Phaser.BlendModes.NORMAL,
        )
      }
    }
  }

  private applySquashAndStretch(
    sx: number,
    sy: number,
    durationMs: number,
  ): void {
    this.tweens.add({
      targets: this.dogSprite,
      scaleX: (SPRITE_SIZES.player_sprite.w / this.dogSprite.width) * sx,
      scaleY: (SPRITE_SIZES.player_sprite.h / this.dogSprite.height) * sy,
      duration: durationMs / 2,
      yoyo: true,
      ease: "Quad.easeOut",
    })
  }

  private onShutdown(): void {
    this.inputManager.destroy()
    EventBus.off(EVENT.POWER_MODE_CHANGED, this.handlePowerModeChanged, this)
    EventBus.off(EVENT.HERO_STATE_CHANGED, this.handleHeroStateChanged, this)
    EventBus.off(EVENT.RUN_STATE_CHANGED, this.handleRunStateChanged, this)
    EventBus.off(EVENT.COMBO_TIER_REACHED, this.handleComboTierReached, this)
    EventBus.off(EVENT.PLAYER_LEVELUP, this.handlePlayerLevelUp, this)
    this.bgLayers = []
    this.bgLayerBaseY = []
    this.bgNoiseOverlay = null
    this.bgDispNoise = null
    this.bgCellNoise = null
    this.bgRiverReflection = null
    this.bgStarContainer = null
    this.bgCityLights = null
    this.bgShootingStarTimer = null
    this.bgScanlines = null
    this.darknessOverlay = undefined
    this.sectorOverlay = null
    this.forceField = undefined
  }

  public startWaveIntro(): void {
    const waveNumber = this.getActiveWaveNumber()
    this.playerRuntime.setMovementStateOverride("launching")
    this.setRunStage("waveIntro")
    this.physics.pause()
    this.isAiming = false

    this.cameras.main.fadeIn(500, 0, 0, 0)
    setMusicWaveVolume(this)
    this.playSfx(AUDIO.WAVE_START, 0.4)

    // Play warp jump sound at sector transitions (first wave of each sector)
    if ((waveNumber - 1) % SECTOR_WAVE_SPAN === 0) {
      this.playSfx(AUDIO.SFX_WARP_JUMP, 0.3)
      this.vfx.emitWarpRipple()
    }

    const isBossWave = this.waveTimeline.isBossWave()
    const isFinalBossWave = waveNumber === MAX_WAVES
    const boss = this.waveTimeline.getCurrentSector().boss

    if (isBossWave) {
      this.time.delayedCall(500, () => this.playSfx(AUDIO.SFX_FLYBY, 0.35))
      this.vfx.applyBossIntro()
      this.time.delayedCall(1600, () => this.vfx.clearBossIntro())

      // Phaser 4 threshold intro pulse
      const threshold = applyThresholdPass(this.cameras.main, 1.0, 1.0)
      this.tweens.add({
        targets: threshold,
        edge1: { from: 1.0, to: 0.0 },
        edge2: { from: 1.0, to: 0.2 },
        duration: 400,
        yoyo: true,
        onComplete: () => {
          this.cameras.main.filters?.external.remove(threshold)
        },
      })
    }

    if (isFinalBossWave) {
      this.playSfx(AUDIO.SFX_ROAR, 0.8)
      this.vfx.flashScreen(COLOR.warning, 0.18, 180)
    }

    this.time.delayedCall(0, () => {
      const hudScene = this.scene.get(SCENE.HUD) as HudScene | undefined
      if (!hudScene) return

      hudScene.showWaveAnnouncement(
        waveNumber,
        this.waveTimeline.getCurrentSector().name,
        isBossWave
          ? `${boss.name} — ${boss.intro}`
          : getWaveAnnouncementMessage(waveNumber),
        isBossWave,
        !isBossWave && waveNumber % SECTOR_WAVE_SPAN === 0,
        SCENE_TUNING.waveIntroDurationMs,
      )
    })

    const body = this.dogSprite.body as Phaser.Physics.Arcade.Body
    this.dogSprite.setPosition(
      SCENE_TUNING.waveIntroStartX,
      SCENE_TUNING.waveIntroY,
    )
    this.dogSprite.setAngle(-12)
    body.setVelocity(0, 0)

    const launchState = { progress: 0 }
    this.vfx.startHeroLaunchExhaust(this.dogSprite)
    this.vfx.sparkEmitter.explode(
      8,
      this.dogSprite.x - 38,
      this.dogSprite.y + 6,
    )
    this.vfx.smokeEmitter.explode(
      6,
      this.dogSprite.x - 38,
      this.dogSprite.y + 6,
    )

    this.tweens.add({
      targets: launchState,
      progress: 1,
      duration: SCENE_TUNING.waveIntroDurationMs,
      ease: "Sine.easeInOut",
      onUpdate: () => {
        const t = launchState.progress
        const waveLift = Math.sin(t * Math.PI * 6) * (18 * (1 - t) + 6)
        const pitch =
          Phaser.Math.Linear(-12, 4, t) + Math.sin(t * Math.PI * 4) * 3
        this.dogSprite.setPosition(
          Phaser.Math.Linear(
            SCENE_TUNING.waveIntroStartX,
            SCENE_TUNING.waveIntroEndX,
            t,
          ),
          Phaser.Math.Linear(
            SCENE_TUNING.waveIntroY,
            SCENE_TUNING.waveIntroY,
            t,
          ) + waveLift,
        )
        this.dogSprite.setAngle(pitch)
      },
      onComplete: () => this.finishWaveIntro(),
    })
  }

  private finishWaveIntro(): void {
    if (this.gameData.state !== "waveIntro") return

    const body = this.dogSprite.body as Phaser.Physics.Arcade.Body
    body.position.set(
      this.dogSprite.x - body.halfWidth,
      this.dogSprite.y - body.halfHeight,
    )
    body.prev.set(body.position.x, body.position.y)
    body.setVelocity(0, 0)
    this.vfx.stopHeroLaunchExhaust()

    if (this.waveTimeline.isBossWave() && !this.bossSpawned) {
      this.launchBossCutscene()
      return
    }

    this.isAiming = false
    this.playerRuntime.setMovementStateOverride(null)
    this.setRunStage("playing")
    this.physics.resume()
  }

  private launchBossCutscene(): void {
    const sector = this.waveTimeline.getCurrentSector()
    fadeMusicOut(this, 900, () => startBossMusic(this, AUDIO.MUSIC_BOSS))
    this.scene.launch(SCENE.BOSS_CUTSCENE, {
      bossName: sector.boss.name,
      bossTagline: sector.boss.intro,
      sectorName: sector.name,
    })
  }

  /** Called by BossCutsceneScene when the intro finishes. */
  public finishBossCutscene(): void {
    if (this.bossSpawned) return
    this.spawnBossEncounter()
    this.isAiming = false
    this.playerRuntime.setMovementStateOverride(null)
    this.setRunStage("playing")
    this.physics.resume()
  }

  /** Called by UpgradeChoiceScene when the player picks an upgrade. */
  public applyUpgradeChoice(upgrade: Upgrade): void {
    this.run.applyUpgradeChoice(upgrade)
    this.syncCombatBallProgression()
  }

  /** Called by UpgradeChoiceScene to get fresh choices on reroll. */
  public getUpgradeChoices(): Upgrade[] {
    return this.run.getUpgradeChoices()
  }

  public getWaveNumber(): number {
    return this.getActiveWaveNumber()
  }

  public isBossWave(): boolean {
    return this.waveTimeline.isBossWave()
  }

  public getDogSprite(): GameSprite {
    return this.dogSprite
  }

  public getGameData(): GameData {
    return this.gameData
  }

  public setDebugFrozen(frozen: boolean): void {
    this._debugFrozen = frozen
  }

  public setInfiniteTime(enabled: boolean): void {
    this._infiniteTime = enabled
  }

  public spawnEnemyAtPoint(type: EnemyType, x: number, y: number): void {
    const waveNumber = this.getActiveWaveNumber()
    const validType = this.getValidEnemyType(type)
    if (!validType) return

    let targetY = y
    if (validType === "router") {
      const lanes = [GAME_HEIGHT * 0.24, GAME_HEIGHT * 0.48, GAME_HEIGHT * 0.74]
      targetY = Math.random() < 0.5 ? lanes[0] : lanes[2]
    }

    const enemy = this.enemies.createEnemyByType(
      validType,
      x,
      targetY,
      waveNumber,
      1,
    )
    const spawnedEnemy = this.enemies.spawnEnemy(enemy)
    if (spawnedEnemy) {
      this.observer.observeSpawn(spawnedEnemy, "encounter")
    }
  }

  public killAllEnemies(): void {
    const enemies = this.enemyGroup.getChildren() as GameSprite[]
    for (const enemy of enemies) {
      if (enemy.active) {
        this.enemyFlow.defeatEnemy(enemy)
      }
    }
  }

  public triggerSectorClear(): void {
    this.waveTimeline.completeBossWave()
    returnToWaveMusic(this, 1800)
    this.startWaveEndSummary()
  }

  private setAllStatsDebugLevel(targetLevel: number): void {
    const desiredLevel = Math.max(1, Math.floor(targetLevel))
    const statUpgradeByLevelKey = new Map(
      STAT_UPGRADES.map((upgrade) => {
        const statKey = Object.keys(upgrade.statBoost ?? {})[0]
        const levelKey = statKey === "maxHp" ? "hp" : statKey
        return [levelKey, upgrade] as const
      }),
    )

    let nextPlayer = this.gameData.player
    for (const [levelKey, currentLevelRaw] of Object.entries(
      nextPlayer.statLevels ?? {},
    )) {
      const upgrade = statUpgradeByLevelKey.get(levelKey)
      if (!upgrade) continue

      let currentLevel = currentLevelRaw
      while (currentLevel < desiredLevel) {
        nextPlayer = applyUpgrade(nextPlayer, upgrade)
        currentLevel += 1
      }
    }

    this.gameData.player = nextPlayer
    GameState.instance.player = nextPlayer
    this.hudSync.sync()
  }

  public getDebugInspectState(): WaveDebugInspectState {
    return {
      gameData: this.gameData,
      dogSprite: this.dogSprite,
      enemyGroup: this.enemyGroup,
      projectileGroup: this.projectileGroup,
      expCoinGroup: this.expCoinGroup,
    }
  }

  public getWaveTimeline(): WaveTimeline {
    return this.waveTimeline
  }

  public getVfx(): VfxManager {
    return this.vfx
  }

  public getEnemies(): EnemySystem {
    return this.enemies
  }

  public getEconomy(): EconomySystem {
    return this.economy
  }

  public playSfx(key: string, volume = 1): void {
    getSfxPlayer(this)?.play(key, volume)
  }

  public syncWaveMetadata(): void {
    const wave = this.waveTimeline.getWave()
    const duration = this.waveTimeline.getCurrentWaveDuration()
    const sector = this.waveTimeline.getCurrentSector()

    if (sector.id !== this.activeSectorId) {
      this.rebuildSectorBackground(sector.id)
    }

    this.gameData.wave = wave
    this.gameData.waveDuration = duration
    this.gameData.waveTimer = this.waveTimeline.getRemainingTime()
    this.gameData.pushProgress = this.waveTimeline.getWaveProgress()
    this.gameData.pushDistance = Math.floor(
      this.gameData.pushProgress * this.gameData.pushTargetDistance,
    )
    const gameState = GameState.instance
    gameState.currentWave = wave
    gameState.score = this.gameData.score
    gameState.coins = this.gameData.coins
    gameState.runDistance = this.gameData.runDistance
  }

  public setDarknessOverlay(alpha: number): void {
    this.updateDarknessOverlay(alpha)
  }

  private launchTestOverlay(): void {
    const wave = this.getActiveWaveNumber()
    const player = this.gameData.player
    const state = GameState.instance

    switch (this.testOverlay) {
      case SCENE.HUD:
        this.scene.launch(SCENE.HUD)
        break
      case SCENE.SHOP:
        this.scene.launch(SCENE.SHOP, {
          wave,
          coins: state.coins,
          coinsThisWave: 0,
          player,
        })
        break
      case SCENE.UPGRADE_CHOICE:
        this.scene.launch(SCENE.UPGRADE_CHOICE, {
          wave,
          choices: getRandomUpgrades(3, player, "level"),
        })
        break
      case SCENE.PAUSE:
        this.scene.launch(SCENE.PAUSE)
        break
      case SCENE.DEBUG:
        this.scene.launch(SCENE.DEBUG)
        break
    }
  }
}
